import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as XLSX from 'xlsx';
import { logger } from '../../shared/utils/logger';
import MonthlyBudgetEditor from './components/MonthlyBudgetEditor';
import { Grid, Users, Building, Package, Plus, Search, Filter, Edit, Trash2, X, Loader2, AlertCircle, AlertTriangle, CheckCircle, RefreshCw, Eye, ChevronDown, ChevronUp, ChevronRight, User, Phone, Mail, MapPin, FileCheck, Settings, Activity, TrendingUp, DollarSign, FileDown, FileText, FileSpreadsheet, FileOutput, XCircle, Layers, Link, Upload, Download, Calendar, FolderTree, Archive, Globe, Save, Hash, LayoutList, List, BookOpen, LayoutGrid } from 'lucide-react';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import { useComptabiliteTableColumnVisibility, useComptabiliteListRowSelection, ComptabiliteBulkDeleteFilterAction, COMPTABILITE_TABLE_HEADER_ACTIONS_CLASS } from './utils/comptabiliteListPageUtils';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import useColumnHeaderFilters from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule } from '../../shared/utils/columnHeaderFilters';
import { formatBrandMoney, getBrandDefaultCurrency, getBrandCurrencySuffix, getBrandLocale } from '../../shared/utils/brandCurrency';
import {
  formatComptabiliteMoney,
  getDefaultChartStandard,
  getChartStandardOptions,
  buildChartStandardMap,
  getAccountClassDefinitions,
  getDefaultLocationPlaceholder } from './utils/comptabiliteSaudiFormat';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import CustomDatePicker from '../../shared/components/custom/CustomDatePicker';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import QawaemErpListPageShell, { sliceQawaemStats } from '../../products/saudi/components/QawaemErpListPageShell';
import '../../shared/styles/GPSTrackingDatePicker.css';
import {
  getAuxiliaries, getAuxiliary, generateAuxiliaryCode, createAuxiliary, updateAuxiliary, deleteAuxiliary,
  getAuxiliariesSummary, getChartOfAccounts,
  createAccount, updateAccount, deleteAccount,
  getAnalyticalCodes, createAnalyticalCode, updateAnalyticalCode, deleteAnalyticalCode,
  getOperations, createOperation, updateOperation, deleteOperation,
  getCodes, createCode, updateCode, deleteCode,
  getAffectations, createAffectation, updateAffectation, deleteAffectation
} from '../../shared/utils/comptabiliteService';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import { useSettings } from '../../core/contexts/SettingsContext';
import { generateEntityReference, getEntityPrefix } from '../../shared/utils/generateEntityReference';
import { dispatchChartAccountsRefresh, CHART_ACCOUNTS_REFRESH_EVENT } from './utils/chartAccountOptions';
import {
  loadAnalyticalSelectOptions,
  dispatchAnalyticalCodesRefresh,
  ANALYTICAL_CODES_REFRESH_EVENT,
} from './utils/comptabiliteAnalyticalOptions';
import { useComptabiliteConfig } from './useComptabiliteConfig';
import {
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
} from '../../products/saudi/components/QawaemTableSelection';

const PLAN_ACCOUNTS_LIST_COLUMNS = [
  { key: 'code', labelKey: 'col_account_code' },
  { key: 'name', labelKey: 'col_label' },
  { key: 'className', labelKey: 'col_class' },
  { key: 'type', labelKey: 'col_type' },
  { key: 'balance', labelKey: 'col_balance' },
  { key: 'standard', labelKey: 'col_standard' },
  { key: 'status', labelKey: 'col_status' },
];

const ANALYTICAL_CODES_LIST_COLUMNS = [
  { key: 'code', labelKey: 'col_code' },
  { key: 'name', labelKey: 'col_name' },
  { key: 'description', labelKey: 'col_description' },
];

const AUXILIARIES_LIST_COLUMNS = [
  { key: 'code', labelKey: 'col_code' },
  { key: 'name', labelKey: 'col_name' },
  { key: 'type', labelKey: 'col_type' },
  { key: 'parent', labelKey: 'col_parent' },
  { key: 'balance', labelKey: 'col_balance' },
  { key: 'status', labelKey: 'col_status' },
];

const OPERATIONS_LIST_COLUMNS = [
  { key: 'operation', labelKey: 'col_operation' },
  { key: 'code', labelKey: 'col_code' },
  { key: 'code_name', labelKey: 'col_code_name' },
  { key: 'category', labelKey: 'col_category' },
  { key: 'frequency', labelKey: 'col_frequency' },
  { key: 'cost', labelKey: 'col_cost' },
  { key: 'priority', labelKey: 'col_priority' },
];

const CODES_LIST_COLUMNS = [
  { key: 'code', labelKey: 'col_code' },
  { key: 'name', labelKey: 'col_name' },
  { key: 'description', labelKey: 'col_description' },
  { key: 'category', labelKey: 'col_category' },
  { key: 'priority', labelKey: 'col_priority' },
  { key: 'status', labelKey: 'col_status' },
];

const AFFECTATIONS_LIST_COLUMNS = [
  { key: 'operation', labelKey: 'col_operation' },
  { key: 'assigned_code', labelKey: 'col_assigned_code' },
  { key: 'type', labelKey: 'col_type' },
  { key: 'date', labelKey: 'col_date' },
  { key: 'responsible', labelKey: 'col_responsible' },
  { key: 'status', labelKey: 'col_status' },
];

const DEMO_ANALYTICAL_CODES = {
  postes: [
    { id: 'p1', code: 'PROJ001', nameKey: 'demo_proj_alpha', descKey: 'demo_proj_alpha_desc', prefix: 'PROJ' },
    { id: 'p2', code: 'PROJ002', nameKey: 'demo_proj_beta', descKey: 'demo_proj_beta_desc', prefix: 'PROJ' },
    { id: 'p3', code: 'ADM001', nameKey: 'demo_admin', descKey: 'demo_admin_desc', prefix: 'ADM' },
  ],
  natures: [
    { id: 'n1', code: 'NAT001', nameKey: 'demo_exploitation', descKey: 'demo_exploitation_desc', prefix: 'NAT' },
    { id: 'n2', code: 'NAT002', nameKey: 'demo_investissement', descKey: 'demo_investissement_desc', prefix: 'NAT' },
    { id: 'n3', code: 'NAT003', nameKey: 'demo_financement', descKey: 'demo_financement_desc', prefix: 'NAT' },
  ],
  unites: [
    { id: 'u1', code: 'UNI001', nameKey: 'demo_siege', descKey: 'demo_siege_desc', prefix: 'UNI' },
    { id: 'u2', code: 'UNI002', nameKey: 'demo_usine', descKey: 'demo_usine_desc', prefix: 'UNI' },
    { id: 'u3', code: 'UNI003', nameKey: 'demo_commercial', descKey: 'demo_commercial_desc', prefix: 'UNI' },
  ] };

const demoAnalyticalLabel = (item, translate) =>
  `${item.code} — ${item.nameKey ? translate(item.nameKey) : item.name}`;

const PlanCollapsibleSection = ({ icon: Icon, iconClass = 'text-blue-400', title, expanded, onToggle, children }) => (
  <div className="bg-white/5 rounded-xl border border-white/20 overflow-hidden">
    <button type="button" onClick={onToggle} className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors">
      <div className="flex items-center gap-3">
        <Icon size={20} className={iconClass} />
        <h3 className="text-lg font-semibold text-white">{title}</h3>
      </div>
      {expanded ? <ChevronUp size={20} className="text-white/60" /> : <ChevronDown size={20} className="text-white/60" />}
    </button>
    <AnimatePresence>
      {expanded && (
        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
          <div className="p-4 pt-0">{children}</div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

/**
 * Analytical Codes Manager (Postes, Natures, Unités)
 * Multi-dimensional analytical tracking
 */
const AnalyticalCodesManager = ({ isOpen, onClose, type = 'postes', chartStandard = getDefaultChartStandard() }) => {
  const { t } = useTranslation();
  const { settings } = useSettings();
  const p = (key, opts) => t(`modules.comptabilite.pages.planComptablePage.${key}`, opts);
  const [activeType, setActiveType] = useState(type);
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [formData, setFormData] = useState({ code: '', name: '', description: '', prefix: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const { filters: codeColumnFilters, setFilter: setCodeColumnFilter, editingKey: codeEditingKey, setEditingKey: setCodeEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const codeColumnFilterRules = useMemo(() => [
    textColumnRule('code', (row) => row.code ?? ''),
    textColumnRule('name', (row) => row.name ?? ''),
    textColumnRule('description', (row) => row.description ?? ''),
  ], []);
  const [loadError, setLoadError] = useState('');
  const [saveHint, setSaveHint] = useState('');
  const [analyticalCodeManagerTab, setAnalyticalCodeManagerTab] = useState('form');

  const typeLabels = {
    postes: { singular: p('type_poste'), plural: p('type_postes'), description: p('type_poste_desc'), icon: Layers },
    natures: { singular: p('type_nature_tab'), plural: p('type_natures'), description: p('type_nature_desc'), icon: Activity },
    unites: { singular: p('type_unite'), plural: p('type_unites'), description: p('type_unite_desc'), icon: Building } };

  const typeTabs = [
    { id: 'postes', name: p('type_postes'), icon: Layers },
    { id: 'natures', name: p('type_natures'), icon: Activity },
    { id: 'unites', name: p('type_unites'), icon: Building },
  ];

  const label = typeLabels[activeType] || typeLabels.postes;

  const generateAnalyticalCode = () => {
    const prefix = getEntityPrefix(settings, 'analyticalCode', 'ANL');
    return generateEntityReference(prefix, codes, (row) => row.code);
  };

  const startNewAnalyticalCode = () => {
    setEditingCode(null);
    setFormData({ code: generateAnalyticalCode(), name: '', description: '', prefix: '' });
    setAnalyticalCodeManagerTab('form');
  };

  useEffect(() => {
    if (isOpen) {
      setActiveType(type);
      setAnalyticalCodeManagerTab('form');
      setFormData({ code: '', name: '', description: '', prefix: '' });
      setEditingCode(null);
      setSearchTerm('');
      setLoadError('');
      setSaveHint('');
    }
  }, [isOpen, type]);

  useEffect(() => {
    if (isOpen) {
      loadCodes(activeType);
    }
  }, [isOpen, activeType]);

  const loadCodes = async (codeType) => {
    setLoading(true);
    setLoadError('');
    try {
      const data = await getAnalyticalCodes(codeType);
      setCodes(sortRecordsNewestFirst(Array.isArray(data) ? data : []));
    } catch (error) {
      logger.error(`Load ${codeType} error:`, error);
      setCodes([]);
      setLoadError(error.message || p('error_load_codes'));
    } finally {
      setLoading(false);
    }
  };

  const handleTypeChange = (codeType) => {
    setActiveType(codeType);
    setEditingCode(null);
    setFormData({ code: '', name: '', description: '', prefix: '' });
    setSearchTerm('');
    setSaveHint('');
  };

  const handleSave = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setSaveHint('');
    const code = (formData.code?.trim() || generateAnalyticalCode());
    const name = formData.name?.trim();
    if (!code || !name) {
      setSaveHint(p('error_code_name_required'));
      return;
    }

    try {
      const payload = {
        code,
        name,
        description: formData.description?.trim() || '',
        prefix: formData.prefix?.trim() || '' };

      if (editingCode?.id) {
        await updateAnalyticalCode(activeType, editingCode.id, payload);
        notifyUpdated(showNotify, p('entity_label', { defaultValue: 'الرمز التحليلي' }));
      } else {
        await createAnalyticalCode(activeType, payload);
        notifyCreated(showNotify, p('entity_label', { defaultValue: 'الرمز التحليلي' }));
      }

      await loadCodes(activeType);
      dispatchAnalyticalCodesRefresh();
      setFormData({ code: '', name: '', description: '', prefix: '' });
      setEditingCode(null);
      setAnalyticalCodeManagerTab('list');
    } catch (error) {
      logger.error('Save error:', error);
      notifyError(showNotify, error.message || p('error_save'));
    }
  };

  const handleEdit = (code) => {
    setEditingCode(code);
    setFormData({
      code: code.code,
      name: code.name,
      description: code.description || '',
      prefix: code.prefix || '' });
    setAnalyticalCodeManagerTab('form');
  };

  const handleView = (code) => {
    handleEdit(code);
  };

  const filteredCodes = codes.filter((c) =>
    c.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedCodes = useMemo(() => applyColumnFilters(filteredCodes, codeColumnFilters, codeColumnFilterRules), [filteredCodes, codeColumnFilters, codeColumnFilterRules]);

  const analyticalCodeManagerTabs = useMemo(() => ([
    { id: 'form', label: editingCode ? p('form_edit', { type: label.singular }) : p('form_new', { type: label.singular }), icon: FileText },
    { id: 'list', label: p('list_title', { plural: label.plural, count: sortedCodes.length }), icon: List },
    { id: 'filters', label: p('filters_search'), icon: Search },
    { id: 'options', label: p('advanced_options_section'), icon: Settings },
  ]), [editingCode, label.singular, label.plural, sortedCodes.length, p]);

  const {
    qawaemColumnDefs: analyticalQawaemColumnDefs,
    handleToggleColumn: handleToggleAnalyticalColumn,
    qawaemVisibleColumnCount: analyticalQawaemVisibleColumnCount,
    isColVisible: isAnalyticalColVisible,
    visibleDataColumnCount: analyticalVisibleDataColumnCount } = useComptabiliteTableColumnVisibility(ANALYTICAL_CODES_LIST_COLUMNS, p);
  const analyticalTableColSpan = analyticalVisibleDataColumnCount + 2;

  const handleDelete = async (id) => {
    if (!window.confirm(p('confirm_delete_code'))) return;

    try {
      await deleteAnalyticalCode(activeType, id);
      notifyDeleted(showNotify, p('entity_label', { defaultValue: 'الرمز التحليلي' }));
      dispatchAnalyticalCodesRefresh();
      await loadCodes(activeType);
    } catch (error) {
      logger.error('Delete error:', error);
      notifyError(showNotify, error.message || p('error_delete'));
    }
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl max-h-[90vh] flex flex-col overflow-hidden"
        style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }}
      >
        <div className="flex items-center justify-between p-6 border-b border-white/10 shrink-0 bg-slate-800/95 backdrop-blur-md">
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Grid className="h-6 w-6 text-blue-400" />
            {p('analytical_title')}
            <span className="text-white/50 text-base font-normal">{p('analytical_plan_suffix', { standard: chartStandard })}</span>
          </h2>
          <div className="flex items-center gap-3">
            <button
              type="submit"
              form="analytical-code-form"
              className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"
            >
              <CheckCircle size={18} />
              {editingCode ? p('btn_update') : p('btn_save')}
            </button>
            <button type="button" onClick={onClose} className="text-white/60 hover:text-white transition-colors">
              <X size={24} />
            </button>
          </div>
        </div>

        <div className="px-6 pt-4 shrink-0">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto hide-scrollbar">
              {typeTabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => handleTypeChange(tab.id)}
                  className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 whitespace-nowrap min-w-[100px] ${
                    activeType === tab.id
                      ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30'
                      : 'text-white/80 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <tab.icon className="h-5 w-5" />
                  <span>{tab.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="overflow-y-auto p-6 flex-1">
          <form id="analytical-code-form" onSubmit={handleSave} className="space-y-4">
            {loadError && (
              <div className="px-4 py-3 rounded-xl bg-red-500/10 border border-red-400/30 text-red-300 text-sm">
                {loadError}
              </div>
            )}
            {saveHint && (
              <div className="px-4 py-3 rounded-xl bg-amber-500/10 border border-amber-400/30 text-amber-200 text-sm">
                {saveHint}
              </div>
            )}
            <div className="shrink-0 border-b border-white/10 -mx-6 px-6 pb-0 mb-4">
              <div className="flex flex-wrap gap-1" role="tablist">
                {analyticalCodeManagerTabs.map((tab) => {
                  const TabIcon = tab.icon;
                  const active = analyticalCodeManagerTab === tab.id;
                  return (
                    <button key={tab.id} type="button" role="tab" aria-selected={active} onClick={() => setAnalyticalCodeManagerTab(tab.id)} className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-t-xl text-sm font-semibold transition-colors ${active ? 'bg-white/10 text-white border-b-2 border-blue-400 -mb-px' : 'text-white/55 hover:text-white hover:bg-white/5'}`}>
                      <TabIcon size={16} />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {analyticalCodeManagerTab === 'form' && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_code_field')}</label>
                  <input type="text" value={formData.code} onChange={(e) => setFormData({ ...formData, code: e.target.value.substring(0, 30) })} placeholder={p('placeholder_code')} maxLength={30} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" />
                </div>
                <div>
                  <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_name_field')}</label>
                  <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value.substring(0, 100) })} placeholder={p('placeholder_name', { type: label.singular.toLowerCase() })} maxLength={100} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" />
                </div>
                <div>
                  <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_prefix')}</label>
                  <input type="text" value={formData.prefix} onChange={(e) => setFormData({ ...formData, prefix: e.target.value })} placeholder={p('placeholder_prefix')} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" />
                </div>
                <div className="md:col-span-3">
                  <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_description')}</label>
                  <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder={p('placeholder_description')} rows={2} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none" />
                </div>
                {editingCode && (
                  <div className="md:col-span-3">
                    <button type="button" onClick={startNewAnalyticalCode} className="px-4 py-2 bg-white/10 border border-white/20 text-white rounded-xl hover:bg-white/15 transition-colors text-sm">
                      {p('cancel_edit')}
                    </button>
                  </div>
                )}
              </div>
            )}

            {analyticalCodeManagerTab === 'list' && (
              <div>
                {loading ? (
                  <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 text-white/40 animate-spin" /></div>
                ) : sortedCodes.length > 0 ? (
                  <div className="overflow-x-auto rounded-xl border border-white/10">
                    <table className="w-full employees-table data-table">
                      <thead className="glass-table-head">
                        <tr>
                          {isAnalyticalColVisible('code') ? (
                          <TableColumnHeaderFilter columnKey="code" label={p('col_code')} value={codeColumnFilters.code || ''} onChange={(v) => setCodeColumnFilter('code', v)} editingKey={codeEditingKey} onEditingKeyChange={setCodeEditingKey} thClassName="text-white" />
                          ) : null}
                          {isAnalyticalColVisible('name') ? (
                          <TableColumnHeaderFilter columnKey="name" label={p('col_name')} value={codeColumnFilters.name || ''} onChange={(v) => setCodeColumnFilter('name', v)} editingKey={codeEditingKey} onEditingKeyChange={setCodeEditingKey} thClassName="text-white" />
                          ) : null}
                          {isAnalyticalColVisible('description') ? (
                          <TableColumnHeaderFilter columnKey="description" label={p('col_description')} value={codeColumnFilters.description || ''} onChange={(v) => setCodeColumnFilter('description', v)} editingKey={codeEditingKey} onEditingKeyChange={setCodeEditingKey} thClassName="text-white" />
                          ) : null}
                          <QawaemTableHeaderActionsCell
                            label={p('col_actions')}
                            columns={analyticalQawaemColumnDefs}
                            onToggle={handleToggleAnalyticalColumn}
                            visibleCount={analyticalQawaemVisibleColumnCount}
                            totalCount={analyticalQawaemColumnDefs.length}
                          thClassName={COMPTABILITE_TABLE_HEADER_ACTIONS_CLASS}
                          />
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/10 employees-table-body">
                        {sortedCodes.map((code, index) => (
                          <tr key={code.id} className="hover:bg-white/5 transition-colors">
                            {isAnalyticalColVisible('code') ? (
                            <td className="px-6 py-4"><span className="text-cyan-400 font-mono font-semibold">{code.code}</span></td>
                            ) : null}
                            {isAnalyticalColVisible('name') ? (
                            <td className="px-6 py-4"><span className="text-white font-semibold">{code.name}</span></td>
                            ) : null}
                            {isAnalyticalColVisible('description') ? (
                            <td className="px-6 py-4"><span className="text-white/70 text-sm">{code.description || p('not_available')}</span></td>
                            ) : null}
                            <td className="px-6 py-4">
                              <QawaemErpRowActions
                                onView={() => handleView(code)}
                                onEdit={() => handleEdit(code)}
                                onDelete={() => handleDelete(code.id)}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-white/5 border-t border-white/10">
                          <td colSpan={analyticalTableColSpan} className="px-6 py-4">
                            <div className="flex items-center justify-between text-white/70 text-sm">
                              <span>{p('codes_displayed', { count: sortedCodes.length })}</span>
                              <span>{p('total_plural', { plural: label.plural })} <span className="text-white font-semibold">{codes.length}</span></span>
                            </div>
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ) : (
                  <p className="text-white/50 text-sm text-center py-6">{p('empty_singular', { type: label.singular.toLowerCase() })}</p>
                )}
              </div>
            )}

            {analyticalCodeManagerTab === 'filters' && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-3">
                  <div className="relative max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                    <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder={p('search_placeholder')} className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                </div>
              </div>
            )}

            {analyticalCodeManagerTab === 'options' && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-3 space-y-3">
                  <p className="text-white/60 text-sm">{p('active_type_hint', { description: label.description, plural: label.plural })}</p>
                  <p className="text-white/40 text-xs">{p('prefix_hint', { type: label.singular })}</p>
                </div>
              </div>
            )}
          </form>
        </div>
      </motion.div>
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </div>,
    document.body
  );
};




/**
 * Modern Excel Import/Export Component
 * Drag-and-drop with live preview
 */
const ExcelImportExport = ({ isOpen, onClose, type = 'accounts' }) => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.comptabilite.pages.planComptablePage.${key}`, opts);
  const [activeTab, setActiveTab] = useState('import');
  const [isDragging, setIsDragging] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [importStatus, setImportStatus] = useState(null);
  const fileInputRef = useRef(null);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      await processFile(files[0]);
    }
  };

  const handleFileSelect = async (e) => {
    const files = e.target.files;
    if (files.length > 0) {
      await processFile(files[0]);
    }
  };

  const processFile = async (file) => {
    try {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet);
        
        setPreviewData(jsonData.slice(0, 10)); // Preview first 10 rows
        setImportStatus({ type: 'success', message: p('rows_detected', { count: jsonData.length }) });
      };

      reader.readAsArrayBuffer(file);
    } catch (error) {
      logger.error('File processing error:', error);
      setImportStatus({ type: 'error', message: p('file_read_error') });
    }
  };

  const handleImport = async () => {
    try {
      // Call backend API
      setImportStatus({ type: 'success', message: p('import_success') });
      setTimeout(() => onClose(), 2000);
    } catch (error) {
      logger.error('Import error:', error);
    }
  };

  const handleExport = () => {
    // Generate Excel export
    const ws = XLSX.utils.json_to_sheet([
      { CODE: '411000', LIBELLE: 'Clients', LETTRABLE: 'X', DEVISE: '', 'S/AUX': 'C' }
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, p('sheet_accounts'));
    XLSX.writeFile(wb, `Export_${type}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={onClose} role="presentation">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-gradient-to-br from-slate-800 via-blue-900/50 to-slate-800 border-2 border-blue-400/50 rounded-2xl p-6 max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white">{p('excel_title')}</h2>
          <button onClick={onClose} className="p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white">
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex space-x-2 mb-6">
          <button
            onClick={() => setActiveTab('import')}
            className={`flex-1 px-4 py-2 rounded-xl transition-all ${
              activeTab === 'import'
                ? 'bg-blue-500/30 text-white border border-blue-400/50'
                : 'bg-white/5 text-white/60 hover:bg-white/10'
            }`}
          >
            <Upload size={16} className="inline mr-2" />
            {p('tab_import')}
          </button>
          <button
            onClick={() => setActiveTab('export')}
            className={`flex-1 px-4 py-2 rounded-xl transition-all ${
              activeTab === 'export'
                ? 'bg-blue-500/30 text-white border border-blue-400/50'
                : 'bg-white/5 text-white/60 hover:bg-white/10'
            }`}
          >
            <Download size={16} className="inline mr-2" />
            {p('tab_export')}
          </button>
        </div>

        {/* Import Tab */}
        {activeTab === 'import' && (
          <div className="flex-1 overflow-y-auto">
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-xl p-12 text-center transition-all ${
                isDragging
                  ? 'border-blue-400 bg-blue-500/10'
                  : 'border-white/20 bg-white/5 hover:border-blue-400/50'
              }`}
            >
              <FileSpreadsheet size={48} className="mx-auto mb-4 text-white/40" />
              <p className="text-white text-lg mb-2">{p('excel_drop')}</p>
              <p className="text-white/50 text-sm mb-4">{p('excel_or')}</p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="bg-blue-500/20 hover:bg-blue-500/30 border border-blue-400/30 text-blue-300 px-6 py-2 rounded-xl font-medium transition-all"
              >
                {p('browse')}
              </button>
            </div>

            {/* Preview */}
            {previewData.length > 0 && (
              <div className="mt-4 bg-white/5 border border-white/10 rounded-xl p-4">
                <h3 className="text-white font-semibold mb-3">{p('preview_title', { count: previewData.length })}</h3>
                <div className="glass-table-scroll overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="glass-table-head">
                      <tr>
                        {Object.keys(previewData[0] || {}).map(key => (
                          <th key={key} className="px-3 py-2 text-left text-white/70">{key}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.slice(0, 5).map((row, i) => (
                        <tr key={i} className="border-t border-white/5">
                          {Object.values(row).map((val, j) => (
                            <td key={j} className="px-3 py-2 text-white/80">{val}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <button
                  onClick={handleImport}
                  className="mt-4 w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-4 py-2 rounded-xl font-medium transition-all"
                >
                  {p('confirm_import')}
                </button>
              </div>
            )}

            {/* Status */}
            {importStatus && (
              <div className={`mt-4 p-4 rounded-xl border flex items-center space-x-2 ${
                importStatus.type === 'success'
                  ? 'bg-green-500/10 border-green-400/30 text-green-300'
                  : 'bg-red-500/10 border-red-400/30 text-red-300'
              }`}>
                {importStatus.type === 'success' ? <CheckCircle size={20} /> : <AlertTriangle size={20} />}
                <span>{importStatus.message}</span>
              </div>
            )}
          </div>
        )}

        {/* Export Tab */}
        {activeTab === 'export' && (
          <div className="flex-1">
            <p className="text-white/60 mb-6">{p('export_hint')}</p>
            <button
              onClick={handleExport}
              className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-6 py-3 rounded-xl font-medium transition-all flex items-center justify-center space-x-2"
            >
              <Download size={20} />
              <span>{p('download_excel')}</span>
            </button>
          </div>
        )}
      </motion.div>
    </div>,
    document.body
  );
};




const ACCOUNT_CLASS_DEFINITIONS = getAccountClassDefinitions();

const mapApiAccountToUi = (acc) => {
  const storedLevel = acc.level != null ? Number(acc.level) : null;
  const code = acc.account_number || acc.code || '';
  const inferredLevel = code.length <= 2 ? 1 : code.length <= 3 ? 2 : 3;
  const level = storedLevel || inferredLevel;
  const uiType = level <= 1 ? 'class' : level === 2 ? 'group' : 'detail';
  return {
    id: acc.id,
    code,
    name: acc.account_name || acc.name || '',
    class: String(acc.class_number ?? acc.class ?? ''),
    type: uiType,
    balance: acc.current_balance ?? acc.opening_balance ?? acc.balance ?? 0,
    debitNature: acc.balance_type !== 'credit',
    isActive: acc.is_active === 1 || acc.is_active === true,
    createdAt: acc.created_at || acc.updated_at || null,
    analyticalPoste: acc.analytical_poste || '',
    analyticalNature: acc.analytical_nature || '' };
};

const RequiredLabel = ({ children }) => (
  <>
    {children}
    <span className="text-red-400 mr-1">*</span>
  </>
);

const buildChartFromAccounts = (accounts) => {
  const byClass = {};
  accounts.forEach((acc) => {
    const classNum = String(acc.class || acc.class_number || acc.code?.charAt(0) || '1');
    if (!byClass[classNum]) byClass[classNum] = [];
    byClass[classNum].push(acc);
  });

  const classKeys = [...new Set([
    ...Object.keys(ACCOUNT_CLASS_DEFINITIONS),
    ...Object.keys(byClass),
  ])].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

  return classKeys.map((classNum) => ({
    class: classNum,
    name: ACCOUNT_CLASS_DEFINITIONS[classNum]?.name || `Classe ${classNum}`,
    color: ACCOUNT_CLASS_DEFINITIONS[classNum]?.color || '#64748b',
    accounts: (byClass[classNum] || []).sort((a, b) => a.code.localeCompare(b.code)) }));
};

const PlanComptablePage = () => {
  const { t } = useTranslation();
  const { settings } = useSettings();
  useComptabiliteConfig();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const p = (key, opts) => t(`modules.comptabilite.pages.planComptablePage.${key}`, opts);
  const chartStandardOptions = getChartStandardOptions();
  const [chartStandard, setChartStandard] = useState(getDefaultChartStandard());
  const fmtMoney = (amount) => formatComptabiliteMoney(amount);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [accountFormData, setAccountFormData] = useState({
    code: '',
    name: '',
    class: '',
    type: '',
    debitNature: false,
    isActive: false,
    analyticalPoste: '',
    analyticalNature: '' });


  const [showImporterPlanComptable, setShowImporterPlanComptable] = useState(false);
  const [showExporterPlanComptable, setShowExporterPlanComptable] = useState(false);
  const [showBudgetEditor, setShowBudgetEditor] = useState(false);
  const [showAnalyticalCodesManager, setShowAnalyticalCodesManager] = useState(false);
  const [analyticalCodesType, setAnalyticalCodesType] = useState('postes');
  const [showExcelImportExport, setShowExcelImportExport] = useState(false);
  const [selectedAccountForBudget, setSelectedAccountForBudget] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('table');
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('general');
  const [quickFilter, setQuickFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [classFilter, setClassFilter] = useState('');
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  const { filters: columnFilters, setFilter: setColumnFilter, editingKey, setEditingKey } = useColumnHeaderFilters();

  const columnFilterRules = useMemo(() => [
    textColumnRule('code', (row) => row.code ?? ''),
    textColumnRule('name', (row) => row.name ?? ''),
    textColumnRule('className', (row) => row.className ?? ''),
    textColumnRule('type', (row) => row.type ?? ''),
    textColumnRule('balance', (row) => String(row.balance ?? '')),
  ], []);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [isTypeFilterOpen, setIsTypeFilterOpen] = useState(false);
  const [analyticalPosteOptions, setAnalyticalPosteOptions] = useState([]);
  const [analyticalNatureOptions, setAnalyticalNatureOptions] = useState([]);
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const typeFilterRef = useRef(null);

  const [apiAccounts, setApiAccounts] = useState([]);

  const loadChartAccounts = async () => {
    const rows = await getChartOfAccounts();
    setApiAccounts(rows.map(mapApiAccountToUi));
    return rows;
  };

  useEffect(() => {
    loadChartAccounts().catch((error) => {
      logger.error('Load chart of accounts error:', error);
    });
  }, []);

  useEffect(() => {
    const onRefresh = () => {
      loadChartAccounts().catch((error) => {
        logger.error('Load chart of accounts error:', error);
      });
    };
    window.addEventListener(CHART_ACCOUNTS_REFRESH_EVENT, onRefresh);
    return () => window.removeEventListener(CHART_ACCOUNTS_REFRESH_EVENT, onRefresh);
  }, []);

  useEffect(() => {
    const loadAnalyticalOptions = async () => {
      try {
        const opts = await loadAnalyticalSelectOptions();
        setAnalyticalPosteOptions(opts.postes);
        setAnalyticalNatureOptions(opts.natures);
      } catch (error) {
        logger.error('Load analytical codes for account form:', error);
        setAnalyticalPosteOptions(
          DEMO_ANALYTICAL_CODES.postes.map((item) => ({ value: item.code, label: demoAnalyticalLabel(item, p) })),
        );
        setAnalyticalNatureOptions(
          DEMO_ANALYTICAL_CODES.natures.map((item) => ({ value: item.code, label: demoAnalyticalLabel(item, p) })),
        );
      }
    };
    loadAnalyticalOptions();
    const onRefresh = () => { void loadAnalyticalOptions(); };
    window.addEventListener(ANALYTICAL_CODES_REFRESH_EVENT, onRefresh);
    return () => window.removeEventListener(ANALYTICAL_CODES_REFRESH_EVENT, onRefresh);
  }, [t]);

  const chartOfAccounts = useMemo(() => {
    const chart = buildChartFromAccounts(apiAccounts);
    return buildChartStandardMap(chart);
  }, [apiAccounts]);

  const DROPDOWN_PANEL_STYLE = {
    position: 'absolute',
    top: 'calc(100% + 8px)',
    right: 0,
    zIndex: 99999,
    backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))',
    boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)' };

  const PLAN_MAIN_TABS = [
    { id: 'general', nameKey: 'tab_overview', icon: LayoutGrid },
    { id: 'by_class', nameKey: 'tab_by_class', icon: Layers },
    { id: 'by_type', nameKey: 'tab_by_type', icon: FolderTree },
    { id: 'by_balance', nameKey: 'tab_by_balance', icon: DollarSign },
  ];

  const PLAN_QUICK_FILTERS = [
    { id: 'all', nameKey: 'filter_all', icon: LayoutGrid },
    { id: 'detail', nameKey: 'filter_detail', icon: FileText },
    { id: 'group', nameKey: 'filter_group', icon: FolderTree },
    { id: 'with_balance', nameKey: 'filter_with_balance', icon: DollarSign },
  ];

  const handleAddAccount = () => {
    const prefix = getEntityPrefix(settings, 'accountChart', 'ACC');
    const nextCode = generateEntityReference(prefix, apiAccounts, (row) => row.code);
    setAccountFormData({
      code: nextCode,
      name: '',
      class: '',
      type: 'detail',
      debitNature: true,
      isActive: true,
      analyticalPoste: '',
      analyticalNature: '' });
        setSelectedAccount(null);
    setShowAccountModal(true);
  };

  const handleEditAccount = (account) => {
    setAccountFormData({
      code: account.code || '',
      name: account.name || '',
      class: account.class || '',
      type: account.type || '',
      debitNature: account.debitNature !== undefined ? account.debitNature : true,
      isActive: account.isActive !== undefined ? account.isActive : true,
      analyticalPoste: account.analyticalPoste || '',
      analyticalNature: account.analyticalNature || '' });
        setSelectedAccount(account);
    setShowAccountModal(true);
  };

  const handleSaveAccount = async () => {
    const prefix = getEntityPrefix(settings, 'accountChart', 'ACC');
    const code = (accountFormData.code?.trim() || generateEntityReference(prefix, apiAccounts, (row) => row.code));
    const name = accountFormData.name?.trim();
    if (!code || !name) {
      window.alert(p('alert_code_name_required'));
      return;
    }
    if (!accountFormData.type) {
      window.alert(p('alert_type_required'));
      return;
    }

    const classNum = String(accountFormData.class || code.charAt(0) || '');
    if (!/^[1-7]$/.test(classNum)) {
      window.alert(p('alert_class_required'));
      return;
    }

    const payload = {
      ...accountFormData,
      code,
      name,
      class: accountFormData.class || classNum };

    try {
      let saved;
      if (selectedAccount?.id) {
        saved = await updateAccount(selectedAccount.id, payload);
        notifyUpdated(showNotify, p('entity_label_account', { defaultValue: 'الحساب' }));
      } else {
        saved = await createAccount(payload);
        notifyCreated(showNotify, p('entity_label_account', { defaultValue: 'الحساب' }));
      }

      if (saved) {
        const mapped = mapApiAccountToUi(saved);
        setApiAccounts((prev) => {
          const exists = prev.some((a) => a.id === mapped.id);
          if (exists) return prev.map((a) => (a.id === mapped.id ? mapped : a));
          return [...prev, mapped];
        });
      }

      await loadChartAccounts();
      dispatchChartAccountsRefresh();
      setShowAccountModal(false);
      setSelectedAccount(null);
      setAccountFormData({
        code: '',
        name: '',
        class: '',
        type: '',
        debitNature: true,
        isActive: true,
        analyticalPoste: '',
        analyticalNature: '' });
    } catch (error) {
      logger.error('Saving account:', error);
      notifyError(showNotify, error.message || p('alert_save_error'));
    }
  };

  const handleDeleteAccount = async (account) => {
    if (!account?.id) return;
    if (!window.confirm(p('confirm_delete_account', { code: account.code, defaultValue: `حذف الحساب ${account.code}؟` }))) return;
    try {
      await deleteAccount(account.id);
      notifyDeleted(showNotify, p('entity_label_account', { defaultValue: 'الحساب' }));
      setSelectedRecords((prev) => prev.filter((id) => id !== account.id));
      await loadChartAccounts();
      dispatchChartAccountsRefresh();
    } catch (error) {
      logger.error('Delete account error:', error);
      notifyError(showNotify, error.message || p('alert_delete_error', { message: error.message }));
    }
  };

  const formatBalance = (balance) => {
    if (!balance) return p('not_available');
    const absBalance = Math.abs(balance);
    const sign = balance >= 0 ? '' : '-';
    return `${sign}${fmtMoney(absBalance)}`;
  };

  const getAccountTypeIcon = (type) => {
    switch(type) {
      case 'class': return <Archive size={16} className="text-blue-400" />;
      case 'group': return <FolderTree size={16} className="text-green-400" />;
      case 'detail': return <FileText size={16} className="text-gray-400" />;
      default: return <FileText size={16} className="text-gray-400" />;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
      if (typeFilterRef.current && !typeFilterRef.current.contains(event.target)) setIsTypeFilterOpen(false);    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const allAccounts = useMemo(() => {
    return (chartOfAccounts[chartStandard] || []).flatMap((cls) =>
      (cls.accounts || []).map((acc) => ({
        ...acc,
        classNumber: cls.class,
        className: cls.name,
        classColor: cls.color }))
    );
  }, [chartOfAccounts, chartStandard]);

  const stats = useMemo(() => {
    const details = allAccounts.filter((a) => a.type === 'detail');
    const groups = allAccounts.filter((a) => a.type === 'group');
    const totalBalance = details.reduce((s, a) => s + (a.balance || 0), 0);
    return {
      total: allAccounts.length,
      details: details.length,
      groups: groups.length,
      classes: chartOfAccounts[chartStandard]?.length || 0,
      totalBalance: fmtMoney(totalBalance) };
  }, [allAccounts, chartStandard]);

  const getDistributionData = () => {
    if (mainViewMode === 'by_class') {
      return (chartOfAccounts[chartStandard] || []).map((cls) => ({
        name: p('dist_class', { num: cls.class }),
        count: (cls.accounts || []).filter((a) => a.type === 'detail').length,
        filterId: cls.class,
        filterKind: 'class',
        color: 'bg-gradient-to-r from-blue-500 to-blue-600' }));
    }
    if (mainViewMode === 'by_type') {
      return [
        { name: p('dist_detail'), count: stats.details, filterId: 'detail', filterKind: 'type', color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { name: p('dist_group'), count: stats.groups, filterId: 'group', filterKind: 'type', color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
      ];
    }
    if (mainViewMode === 'by_balance') {
      const positive = allAccounts.filter((a) => (a.balance || 0) > 0).length;
      const negative = allAccounts.filter((a) => (a.balance || 0) < 0).length;
      const zero = allAccounts.filter((a) => !a.balance).length;
      return [
        { name: p('dist_debit_balance'), count: positive, filterId: 'debit', filterKind: 'balance', color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { name: p('dist_credit_balance'), count: negative, filterId: 'credit', filterKind: 'balance', color: 'bg-gradient-to-r from-red-500 to-red-600' },
        { name: p('dist_no_balance'), count: zero, filterId: 'zero', filterKind: 'balance', color: 'bg-gradient-to-r from-orange-500 to-orange-600' },
      ];
    }
    return [
      { name: p('dist_detail_accounts'), count: stats.details, filterId: 'detail', filterKind: 'type', color: 'bg-gradient-to-r from-green-500 to-green-600' },
      { name: p('dist_group_accounts'), count: stats.groups, filterId: 'group', filterKind: 'type', color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
      { name: p('dist_classes'), count: stats.classes, filterId: 'all', filterKind: 'all', color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
      { name: p('dist_active_plan'), count: stats.total, filterId: 'all', filterKind: 'all', color: 'bg-gradient-to-r from-orange-500 to-orange-600' },
    ];
  };

  const distributionInfo = { title: p('dist_title'), data: getDistributionData() };

  const filteredAccounts = useMemo(() => {
    let result = allAccounts;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      result = result.filter((a) => a.code.toLowerCase().includes(q) || a.name.toLowerCase().includes(q));
    }
    if (classFilter) result = result.filter((a) => String(a.classNumber) === String(classFilter));
    if (typeFilter !== 'all') result = result.filter((a) => a.type === typeFilter);
    if (quickFilter === 'detail') result = result.filter((a) => a.type === 'detail');
    if (quickFilter === 'group') result = result.filter((a) => a.type === 'group');
    if (quickFilter === 'with_balance') result = result.filter((a) => a.balance);
    if (activeDistributionFilter) {
      const item = distributionInfo.data.find((d) => d.name === activeDistributionFilter);
      if (item?.filterKind === 'class') result = result.filter((a) => String(a.classNumber) === String(item.filterId));
      if (item?.filterKind === 'type') result = result.filter((a) => a.type === item.filterId);
      if (item?.filterKind === 'balance') {
        if (item.filterId === 'debit') result = result.filter((a) => (a.balance || 0) > 0);
        if (item.filterId === 'credit') result = result.filter((a) => (a.balance || 0) < 0);
        if (item.filterId === 'zero') result = result.filter((a) => !a.balance);
      }
    }
    return applyColumnFilters(result, columnFilters, columnFilterRules);
  }, [allAccounts, searchTerm, classFilter, typeFilter, quickFilter, activeDistributionFilter, columnFilters, columnFilterRules, distributionInfo.data]);

  const {
    selectedRecords: selectedRecords,
    setSelectedRecords: setSelectedRecords,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord: handleSelectRecordBase,
    clearSelection,
  } = useComptabiliteListRowSelection(filteredAccounts, (row) => row.id);
  const handleSelectRecord = handleSelectRecordBase;
  
  

  const totalBalanceFiltered = filteredAccounts.reduce((s, a) => s + (a.balance || 0), 0);

  const {
    qawaemColumnDefs: planQawaemColumnDefs,
    handleToggleColumn: handleTogglePlanColumn,
    qawaemVisibleColumnCount: planQawaemVisibleColumnCount,
    isColVisible: isPlanColVisible,
    visibleDataColumnCount: planVisibleDataColumnCount } = useComptabiliteTableColumnVisibility(PLAN_ACCOUNTS_LIST_COLUMNS, p);
  const planAccountsTableColSpan = planVisibleDataColumnCount + 2;

  const handleAdvancedMainTab = (tabId) => {
    setMainViewMode(tabId);
    setQuickFilter('all');
    setActiveDistributionFilter(null);
    setClassFilter('');
    setTypeFilter('all');
  };

  const handleQuickFilter = (filterId) => {
    setQuickFilter(filterId);
    setActiveDistributionFilter(null);
  };

  const handleDistributionFilter = (item) => {
    const isActive = activeDistributionFilter === item.name;
    setActiveDistributionFilter(isActive ? null : item.name);
    if (item.filterKind === 'class') setClassFilter(isActive ? '' : item.filterId);
    if (item.filterKind === 'type') setTypeFilter(isActive ? 'all' : item.filterId);
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(p('confirm_bulk_delete', { count: selectedRecords.length }))) return;
    try {
      await Promise.all(selectedRecords.map((id) => deleteAccount(id)));
      notifyBulkDeleted(showNotify, selectedRecords.length, p('entity_label_account_plural', { defaultValue: 'الحسابات' }));
      await loadChartAccounts();
      dispatchChartAccountsRefresh();
      setSelectedRecords([]);
      clearSelection();
        } catch (error) {
      logger.error('Bulk delete error:', error);
      notifyError(showNotify, error.message || p('alert_delete_error', { message: error.message }));
    }
  };

  const getExportData = () => (
    selectedRecords.length > 0
      ? filteredAccounts.filter((a) => selectedRecords.includes(a.id))
      : filteredAccounts
  );

  const handleExportJSON = () => {
    const blob = new Blob([JSON.stringify(getExportData(), null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `plan_comptable_${chartStandard}_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setShowExportDropdown(false);
  };

  const handleExportCSV = () => {
    const rows = getExportData();
    const headers = [p('col_code'), p('col_label'), p('col_class'), p('col_type'), p('col_balance')];
    const csv = [headers.join(';'), ...rows.map((a) => [a.code, a.name, a.className, a.type, a.balance || 0].join(';'))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `plan_comptable_${chartStandard}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setShowExportDropdown(false);
  };

  const handleImportExcel = () => {
    setShowExcelImportExport(true);
    setShowImportDropdown(false);
  };

  const handleImportJSON = () => {
    setShowExcelImportExport(true);
    setShowImportDropdown(false);
  };

  const getTypeLabel = (type) => {
    if (type === 'detail') return p('type_detail');
    if (type === 'group') return p('type_group');
    if (type === 'class') return p('type_class');
    return type || p('not_available');
  };

  const renderColumnFilter = (key, label, align = 'left') => (
    <TableColumnHeaderFilter
      columnKey={key}
      label={label}
      value={columnFilters[key] || ''}
      onChange={(v) => setColumnFilter(key, v)}
      editingKey={editingKey}
      onEditingKeyChange={setEditingKey}
      align={align === 'center' ? 'center' : 'start'}
      thClassName="text-white"
    />
  );

  const statsCards = [
    { titleKey: 'stat_total_accounts', value: stats.total, icon: BookOpen, color: 'from-blue-500 to-blue-600' },
    { titleKey: 'stat_detail_accounts', value: stats.details, icon: FileText, color: 'from-green-500 to-green-600' },
    { titleKey: 'stat_group_accounts', value: stats.groups, icon: FolderTree, color: 'from-orange-500 to-orange-600' },
    { titleKey: 'stat_classes', value: stats.classes, icon: Layers, color: 'from-purple-500 to-purple-600' },
    { titleKey: 'stat_total_balance', value: stats.totalBalance, icon: DollarSign, color: 'from-red-500 to-red-600' },
  ];

  const qawaemStats = useMemo(() => sliceQawaemStats(
    statsCards.slice(0, 4).map((s, i) => ({
      key: s.titleKey,
      label: p(s.titleKey),
      value: s.value,
      icon: s.icon,
      variant: i % 2 ? 'forest' : 'copper' })),
    4,
  ), [statsCards, p]);

  const quickFilterLabel = p(PLAN_QUICK_FILTERS.find((f) => f.id === quickFilter)?.nameKey || 'filter_all');

  return (
    <QawaemErpListPageShell
      title={p('title', { standard: chartStandard })}
      subtitle={p('subtitle', { standard: chartStandard })}
      icon={BookOpen}
      stats={qawaemStats}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      filterActions={(
        <ComptabiliteBulkDeleteFilterAction
          count={selectedRecords.length}
          onClick={handleBulkDelete}
          label={p('delete_with_count', { count: selectedRecords.length, defaultValue: `حذف (${selectedRecords.length})` })}
          title={p('delete_with_count', { count: selectedRecords.length, defaultValue: `حذف (${selectedRecords.length})` })}
        />
      )}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder={p('search_placeholder', { defaultValue: 'بحث…' })}
      onCreate={handleAddAccount}
      createLabel={p('new_account')}
      importExport={{
        importOpen: showImportDropdown,
        exportOpen: showExportDropdown,
        onToggleImport: () => { setShowImportDropdown(!showImportDropdown); setShowExportDropdown(false); },
        onToggleExport: () => { setShowExportDropdown(!showExportDropdown); setShowImportDropdown(false); },
        importRef: importDropdownRef,
        exportRef: exportDropdownRef,
        importLabel: p('import'),
        exportLabel: p('export'),
        generatingLabel: t('common.loading'),
        onImportCSV: handleImportJSON,
        onImportExcel: handleImportExcel,
        onExportCSV: handleExportCSV,
        onExportExcel: () => setShowExcelImportExport(true),
        onExportPDF: handleExportJSON }}
      headerActions={(
        <>
          <button
            type="button"
            onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
            className={`${showAdvancedOptions ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400 shadow-lg shadow-blue-500/30' : 'bg-white/5 border-white/20'} border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}
          >
            <Filter className="h-5 w-5" />
            <span className="hidden sm:inline">{p('advanced_options')}</span>
            {showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <button
            type="button"
            onClick={() => { setAnalyticalCodesType('postes'); setShowAnalyticalCodesManager(true); }}
            className="bg-white/5 border border-white/20 hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2"
          >
            <Grid className="h-5 w-5" />
            <span className="hidden sm:inline">{p('analytical_codes')}</span>
          </button>
        </>
      )}
    >
      <div className="flex items-center flex-wrap gap-2 mb-2">
        {chartStandardOptions.map((opt) => (
          <button
            type="button"
            key={opt.id}
            onClick={() => setChartStandard(opt.id)}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              chartStandard === opt.id
                ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30'
                : 'bg-white/10 text-white/70 hover:bg-white/15 border border-white/20'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <motion.div initial={false} animate={{ height: showAdvancedOptions ? 'auto' : 0, opacity: showAdvancedOptions ? 1 : 0 }} transition={{ duration: 0.3, ease: 'easeInOut' }} style={{ overflow: 'hidden' }}>
        <div className="space-y-4 pt-4">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {PLAN_MAIN_TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => handleAdvancedMainTab(tab.id)}
                  className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}
                >
                  <tab.icon className="h-5 w-5" />
                  <span className="hidden sm:inline whitespace-nowrap">{p(tab.nameKey)}</span>
                </button>
              ))}
            </div>
          </div>
          {mainViewMode === 'general' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
              <div className="flex space-x-1 overflow-x-auto table-scroll">
                {PLAN_QUICK_FILTERS.map((filter) => (
                  <button
                    key={filter.id}
                    type="button"
                    onClick={() => handleQuickFilter(filter.id)}
                    className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}
                  >
                    <filter.icon className="h-5 w-5" />
                    <span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
              {activeDistributionFilter && (
                <button type="button" onClick={() => { setActiveDistributionFilter(null); setClassFilter(''); setTypeFilter('all'); }} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1">
                  <XCircle size={14} />
                  {p('reset_filter')}
                </button>
              )}
            </div>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '4')}>
              {distributionInfo.data.map((item) => (
                <button
                  key={item.name}
                  type="button"
                  onClick={() => handleDistributionFilter(item)}
                  className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === item.name ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`}
                >
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-medium ${activeDistributionFilter === item.name ? 'text-white' : 'text-white/80'}`}>{item.name}</h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div
                      className={`h-3 rounded-full ${item.color} transition-all`}
                      style={{
                        width: `${(() => {
                          const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                          return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                        })()}%` }}
                    />
                  </div>
                  <p className={`mt-2 text-sm ${activeDistributionFilter === item.name ? 'text-white/80' : 'text-white/60'}`}>
                    {(() => {
                      const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                      const pct = total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                      return p('percent_of_total', { percent: pct });
                    })()}
                  </p>
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.div>

      <div className="glass-panel glass-table-shell overflow-visible">
        

        {viewMode === 'table' && (
          <div className="glass-table-scroll overflow-x-auto rounded-b-3xl">
            <table className="w-full employees-table data-table">
                    <thead className="glass-table-head">
                      <tr>
                  <QawaemTableSelectAllHeader
                    allRowsSelected={allRowsSelected}
                    someRowsSelected={someRowsSelected}
                    onChange={handleSelectAll}
                  />
                  {isPlanColVisible('code') ? renderColumnFilter('code', p('col_account_code')) : null}
                  {isPlanColVisible('name') ? renderColumnFilter('name', p('col_label')) : null}
                  {isPlanColVisible('className') ? renderColumnFilter('className', p('col_class')) : null}
                  {isPlanColVisible('type') ? renderColumnFilter('type', p('col_type')) : null}
                  {isPlanColVisible('balance') ? renderColumnFilter('balance', p('col_balance')) : null}
                  {isPlanColVisible('standard') ? (
                    <th className="px-6 py-4 text-left text-white font-semibold">{p('col_standard')}</th>
                  ) : null}
                  {isPlanColVisible('status') ? (
                    <th className="px-6 py-4 text-center text-white font-semibold">{p('col_status')}</th>
                  ) : null}
                  <QawaemTableHeaderActionsCell
                    label={p('col_actions')}
                    columns={planQawaemColumnDefs}
                    onToggle={handleTogglePlanColumn}
                    visibleCount={planQawaemVisibleColumnCount}
                    totalCount={planQawaemColumnDefs.length}
                  thClassName={COMPTABILITE_TABLE_HEADER_ACTIONS_CLASS}
                  />
                      </tr>
                    </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {filteredAccounts.length > 0 ? (
                  filteredAccounts.map((account, index) => (
                    <tr
                      key={account.id}
                      className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(account.id) ? 'bg-white/10' : ''}`}
                    >
                      <QawaemTableSelectRowCell
                        checked={selectedRecords.includes(account.id)}
                        onChange={() => handleSelectRecord(account.id)}
                      />
                      {isPlanColVisible('code') ? (
                      <td className="px-6 py-4">
                            <div className="flex items-center space-x-2">
                              {getAccountTypeIcon(account.type)}
                          <span className="text-cyan-400 font-mono font-semibold">{account.code}</span>
                            </div>
                          </td>
                      ) : null}
                      {isPlanColVisible('name') ? (
                      <td className="px-6 py-4"><span className="text-white font-semibold">{account.name}</span></td>
                      ) : null}
                      {isPlanColVisible('className') ? (
                      <td className="px-6 py-4"><span className="text-white/70 text-sm">{account.className}</span></td>
                      ) : null}
                      {isPlanColVisible('type') ? (
                      <td className="px-6 py-4">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              account.type === 'detail' 
                                ? 'bg-green-500/20 text-green-300'
                                : account.type === 'group'
                                ? 'bg-blue-500/20 text-blue-300'
                                : 'bg-purple-500/20 text-purple-300'
                            }`}>
                          {getTypeLabel(account.type)}
                            </span>
                          </td>
                      ) : null}
                      {isPlanColVisible('balance') ? (
                      <td className="px-6 py-4">
                        <span className={`font-bold text-lg ${
                          (account.balance || 0) > 0 ? 'text-green-400' : (account.balance || 0) < 0 ? 'text-red-400' : 'text-white/70'
                            }`}>
                              {formatBalance(account.balance)}
                            </span>
                          </td>
                      ) : null}
                      {isPlanColVisible('standard') ? (
                      <td className="px-6 py-4"><span className="text-white/70 text-sm">{chartStandard}</span></td>
                      ) : null}
                      {isPlanColVisible('status') ? (
                      <td className="px-6 py-4 text-center">
                        <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${account.isActive ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'}`}>
                          {account.isActive ? p('active_account') : p('inactive_account', { defaultValue: 'غير نشط' })}
                        </span>
                      </td>
                      ) : null}
                      <td className="px-6 py-4">
                        <QawaemErpRowActions
                            onView={() => handleEditAccount(account)}
                            onEdit={() => handleEditAccount(account)}
                            onDelete={() => handleDeleteAccount(account)}
                          />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={planAccountsTableColSpan} className="text-center py-12 text-white/60">
                      <BookOpen size={48} className="mx-auto mb-4 text-white/30" />
                      <p className="text-lg">{p('empty_accounts')}</p>
                          </td>
                        </tr>
                )}
                    </tbody>
              <tfoot>
                <tr className="bg-white/5 border-t border-white/10">
                  <td colSpan={planAccountsTableColSpan} className="px-6 py-4">
                    <div className="flex items-center justify-between text-white/70 text-sm flex-wrap gap-2">
                      <span>{p('footer_accounts_total', { count: filteredAccounts.length })}</span>
                      <span>
                        {p('footer_detail_count')}{' '}
                        <span className="text-white font-semibold">{filteredAccounts.filter((a) => a.type === 'detail').length}</span>
                      </span>
                      <span>
                        {p('footer_total_balance')}{' '}
                        <span className="text-white font-semibold">{fmtMoney(totalBalanceFiltered)}</span>
                      </span>
                    </div>
                  </td>
                </tr>
              </tfoot>
                  </table>
              </div>
            )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4 rounded-b-3xl">
            {filteredAccounts.length > 0 ? (
              filteredAccounts.map((account, index) => (
          <motion.div
                  key={account.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-4 mb-4 flex-wrap gap-2">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          <BookOpen size={20} className="text-blue-400" />
                          {account.code}
                        </h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          account.type === 'detail' ? 'bg-green-500/20 text-green-300' : account.type === 'group' ? 'bg-blue-500/20 text-blue-300' : 'bg-purple-500/20 text-purple-300'
                        }`}>
                          {getTypeLabel(account.type)}
                        </span>
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-white/20 text-white/80">{chartStandard}</span>
                </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                          <h5 className="font-medium text-white mb-2">{p('card_identification')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_code')}</span> <span className="text-cyan-400 font-mono">{account.code}</span></div>
                            <div><span className="font-medium">{p('label_name')}</span> {account.name}</div>
                            <div><span className="font-medium">{p('label_standard')}</span> {chartStandard}</div>
                </div>
              </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_classification')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div className="flex items-center gap-2">
                              <Layers size={16} className="text-purple-400" />
                              <span className="font-medium">{p('label_class')}</span> {account.classNumber} — {account.className}
                            </div>
                            <div className="flex items-center gap-2">
                              <FolderTree size={16} className="text-green-400" />
                              <span className="font-medium">{p('label_type')}</span> {getTypeLabel(account.type)}
                            </div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_financial')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            {account.balance ? (
                              <div className={`p-2 rounded border ${(account.balance || 0) >= 0 ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
                                <span className={`font-medium text-xs ${(account.balance || 0) >= 0 ? 'text-green-300' : 'text-red-300'}`}>{p('label_balance')}</span>
                                <p className={`text-sm mt-1 font-semibold ${(account.balance || 0) >= 0 ? 'text-green-200' : 'text-red-200'}`}>{formatBalance(account.balance)}</p>
                              </div>
                            ) : (
                              <div><span className="font-medium">{p('label_balance')}</span> —</div>
                            )}
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_settings')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_nature')}</span> {(account.balance || 0) >= 0 ? p('nature_debit') : p('nature_credit')}</div>
                            <div><span className="font-medium">{p('label_class_color')}</span>
                              <span className="inline-block w-3 h-3 rounded-full ml-2 align-middle" style={{ backgroundColor: account.classColor }} />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => handleEditAccount(account)}
                      onEdit={() => handleEditAccount(account)}
                      onDelete={() => handleDeleteAccount(account)}
                      viewTitle={p('view')}
                      editTitle={p('edit')}
                      deleteTitle={p('delete')}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <BookOpen size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{p('empty_accounts')}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Nouveau Compte Modal */}
      {showAccountModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={() => setShowAccountModal(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl max-h-[90vh] flex flex-col overflow-hidden"
            style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }}
          >
            <div className="flex items-center justify-between p-6 border-b border-white/10 shrink-0 bg-slate-800/95 backdrop-blur-md">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <FolderTree className="h-6 w-6 text-blue-400" />
                {selectedAccount ? p('modal_edit_account') : p('modal_new_account')}
                <span className="text-white/50 text-base font-normal">{p('modal_plan_suffix', { standard: chartStandard })}</span>
              </h2>
              <div className="flex items-center gap-3">
              <button
                  type="submit"
                  form="plan-account-form"
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"
                >
                  <CheckCircle size={18} />
                  {selectedAccount ? p('btn_update') : p('btn_save')}
                </button>
                <button type="button" onClick={() => setShowAccountModal(false)} className="text-white/60 hover:text-white transition-colors">
                  <X size={24} />
              </button>
              </div>
            </div>

            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form
                id="plan-account-form"
                onSubmit={(e) => { e.preventDefault(); handleSaveAccount(); }}
              >
                <div className="space-y-4 pb-6 border-b border-white/10">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right"><RequiredLabel>{p('label_account_code')}</RequiredLabel></label>
                            <input type="text" required value={accountFormData.code} onChange={(e) => setAccountFormData({ ...accountFormData, code: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={chartStandard === 'PCN' ? p('placeholder_code_pcn') : chartStandard === 'SOCPA' ? (p('placeholder_code_socpa') || '1001') : p('placeholder_code_nscf')} />
                        </div>
                        <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right"><RequiredLabel>{p('label_account_name')}</RequiredLabel></label>
                            <input type="text" required value={accountFormData.name} onChange={(e) => setAccountFormData({ ...accountFormData, name: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_clients')} />
                        </div>
                        <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right"><RequiredLabel>{p('label_account_class', { standard: chartStandard })}</RequiredLabel></label>
                            <CustomSelect
                            value={accountFormData.class}
                              onChange={(val) => setAccountFormData({ ...accountFormData, class: val })}
                              options={(chartOfAccounts[chartStandard] || []).map((c) => ({ value: c.class, label: p('class_option', { num: c.class, name: c.name }) }))}
                              placeholder={p('choose_class')}
                              searchable={true}
                              creatable={true}
                              autoSelect={false}
                            />
                        </div>
                  </div>
                </div>
                <div className="space-y-4 pb-6 border-b border-white/10">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right"><RequiredLabel>{p('label_account_type')}</RequiredLabel></label>
                            <CustomSelect
                              value={accountFormData.type}
                              onChange={(val) => setAccountFormData({ ...accountFormData, type: val })}
                              options={[
                                { value: 'detail', label: p('type_detail') },
                                { value: 'group', label: p('type_group') },
                                { value: 'class', label: p('type_class') },
                              ]}
                              placeholder={p('choose_type')}
                              searchable={true}
                              creatable={true}
                              autoSelect={false}
                            />
                          </div>
                  </div>
                </div>
                <div className="space-y-4 pb-6 border-b border-white/10">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <label className="flex items-center gap-3 cursor-pointer">
                            <input type="checkbox" checked={accountFormData.debitNature} onChange={(e) => setAccountFormData({ ...accountFormData, debitNature: e.target.checked })} className="w-4 h-4 rounded border-white/20 bg-white/10 text-blue-500 focus:ring-2 focus:ring-blue-500" />
                            <span className="text-white/80 text-sm">{p('debit_nature')}</span>
                        </label>
                          <label className="flex items-center gap-3 cursor-pointer">
                            <input type="checkbox" checked={accountFormData.isActive} onChange={(e) => setAccountFormData({ ...accountFormData, isActive: e.target.checked })} className="w-4 h-4 rounded border-white/20 bg-white/10 text-blue-500 focus:ring-2 focus:ring-blue-500" />
                            <span className="text-white/80 text-sm">{p('active_account')}</span>
                        </label>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('analytical_poste')}</label>
                            <CustomSelect
                              value={accountFormData.analyticalPoste}
                              onChange={(val) => setAccountFormData({ ...accountFormData, analyticalPoste: val })}
                              options={analyticalPosteOptions.length ? analyticalPosteOptions : DEMO_ANALYTICAL_CODES.postes.map((item) => ({ value: item.code, label: demoAnalyticalLabel(item, p) }))}
                              placeholder={p('choose_poste')}
                              searchable={true}
                              creatable={true}
                              autoSelect={false}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('analytical_nature')}</label>
                            <CustomSelect
                              value={accountFormData.analyticalNature}
                              onChange={(val) => setAccountFormData({ ...accountFormData, analyticalNature: val })}
                              options={analyticalNatureOptions.length ? analyticalNatureOptions : DEMO_ANALYTICAL_CODES.natures.map((item) => ({ value: item.code, label: demoAnalyticalLabel(item, p) }))}
                              placeholder={p('choose_nature')}
                              searchable={true}
                              creatable={true}
                              autoSelect={false}
                            />
                          </div>
                          <div>
                            <button type="button" onClick={() => { setShowAccountModal(false); setShowAnalyticalCodesManager(true); }} className="text-sm text-blue-400 hover:text-blue-300 transition-colors flex items-center gap-2">
                              <Grid size={14} />
                              {p('manage_analytical')}
              </button>
                          </div>
                  </div>
                </div>
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      {/* Importer Plan Comptable Modal */}
      {showImporterPlanComptable && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={() => setShowImporterPlanComptable(false)} role="presentation">
          <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl border border-white/20 p-6 max-w-2xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white">{p('import_modal_title')}</h2>
              <button 
                onClick={() => setShowImporterPlanComptable(false)}
                className="text-white/60 hover:text-white transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="space-y-6">
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <p className="text-blue-300 text-sm">
                  <strong>{p('import')}:</strong> {p('import_modal_hint')}
                </p>
              </div>

              <div className="bg-white/5 rounded-xl p-6 border-2 border-dashed border-white/20 hover:border-blue-500/50 transition-all cursor-pointer">
                <div className="text-center">
                  <Upload size={48} className="mx-auto text-white/40 mb-4" />
                  <p className="text-white font-medium mb-2">{p('import_drop')}</p>
                  <p className="text-white/60 text-sm mb-4">{p('import_or_browse')}</p>
                  <button className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-all">
                    {p('browse_files')}
                  </button>
                </div>
              </div>

              <div className="bg-white/5 rounded-xl p-6">
                <h3 className="text-white font-semibold mb-4">{p('accepted_formats')}</h3>
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-white/5 rounded-lg p-3 text-center">
                    <FileSpreadsheet className="mx-auto text-green-400 mb-2" size={24} />
                    <span className="text-white text-sm">.xlsx</span>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3 text-center">
                    <FileSpreadsheet className="mx-auto text-green-400 mb-2" size={24} />
                    <span className="text-white text-sm">.xls</span>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3 text-center">
                    <FileText className="mx-auto text-blue-400 mb-2" size={24} />
                    <span className="text-white text-sm">.csv</span>
                  </div>
                </div>
              </div>

              <div className="bg-white/5 rounded-xl p-6">
                <h3 className="text-white font-semibold mb-4">{p('import_options')}</h3>
                <div className="space-y-3">
                  <label className="flex items-center space-x-3 text-white">
                    <input type="checkbox" defaultChecked className="rounded" />
                    <span>{p('merge_existing')}</span>
                  </label>
                  <label className="flex items-center space-x-3 text-white">
                    <input type="checkbox" className="rounded" />
                    <span>{p('replace_existing')}</span>
                  </label>
                  <label className="flex items-center space-x-3 text-white">
                    <input type="checkbox" defaultChecked className="rounded" />
                    <span>{p('validate_before_import')}</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Exporter Plan Comptable Modal */}
      {showExporterPlanComptable && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={() => setShowExporterPlanComptable(false)} role="presentation">
          <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl border border-white/20 p-6 max-w-2xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white">{p('export_modal_title')}</h2>
              <button 
                onClick={() => setShowExporterPlanComptable(false)}
                className="text-white/60 hover:text-white transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="space-y-6">
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                <p className="text-green-300 text-sm">
                  <strong>{p('export')}:</strong> {p('export_modal_hint', { standard: chartStandard })}
                </p>
              </div>

              <div className="bg-white/5 rounded-xl p-6">
                <h3 className="text-white font-semibold mb-4">{p('export_format')}</h3>
                <div className="space-y-3">
                  <label className="flex items-center space-x-3 text-white">
                    <input type="radio" name="exportFormat" defaultChecked className="rounded-full" />
                    <span>{p('export_excel_recommended')}</span>
                  </label>
                  <label className="flex items-center space-x-3 text-white">
                    <input type="radio" name="exportFormat" className="rounded-full" />
                    <span>{p('export_csv_text')}</span>
                  </label>
                  <label className="flex items-center space-x-3 text-white">
                    <input type="radio" name="exportFormat" className="rounded-full" />
                    <span>{p('export_pdf_print')}</span>
                  </label>
                </div>
              </div>

              <div className="bg-white/5 rounded-xl p-6">
                <h3 className="text-white font-semibold mb-4">{p('export_content')}</h3>
                <div className="space-y-3">
                  <label className="flex items-center space-x-3 text-white">
                    <input type="checkbox" defaultChecked className="rounded" />
                    <span>{p('export_account_numbers')}</span>
                  </label>
                  <label className="flex items-center space-x-3 text-white">
                    <input type="checkbox" defaultChecked className="rounded" />
                    <span>{p('export_labels')}</span>
                  </label>
                  <label className="flex items-center space-x-3 text-white">
                    <input type="checkbox" defaultChecked className="rounded" />
                    <span>{p('export_account_types')}</span>
                  </label>
                  <label className="flex items-center space-x-3 text-white">
                    <input type="checkbox" className="rounded" />
                    <span>{p('export_balances')}</span>
                  </label>
                  <label className="flex items-center space-x-3 text-white">
                    <input type="checkbox" className="rounded" />
                    <span>{p('export_analytical')}</span>
                  </label>
                </div>
              </div>

              <button className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3 rounded-lg font-semibold hover:from-green-700 hover:to-emerald-700 transition-all">
                <Download size={20} className="inline mr-2" />
                {p('export_button')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      <AnalyticalCodesManager isOpen={showAnalyticalCodesManager} onClose={() => setShowAnalyticalCodesManager(false)} type={analyticalCodesType} chartStandard={chartStandard} />
      <ExcelImportExport isOpen={showExcelImportExport} onClose={() => setShowExcelImportExport(false)} type="accounts" />
      <MonthlyBudgetEditor
        isOpen={showBudgetEditor}
        onClose={() => setShowBudgetEditor(false)}
        accountNumber={selectedAccountForBudget?.account_number}
        accountName={selectedAccountForBudget?.account_name}
      />
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </QawaemErpListPageShell>
  );
};

export default PlanComptablePage;

export const AuxiliairesPage = () => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.comptabilite.pages.auxiliairesPage.${key}`, opts);
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();

  const [loading, setLoading] = useState(false);
  const [auxiliaries, setAuxiliaries] = useState([]);
  const [parentAccounts, setParentAccounts] = useState([]);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [parentFilter, setParentFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingAux, setEditingAux] = useState(null);
  const [viewingAux, setViewingAux] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [stats, setStats] = useState(null);

  const [formData, setFormData] = useState({
    code: '',
    parentAccountNumber: '',
    name: '',
    nameAr: '',
    type: 'client',
    address: '',
    phone: '',
    email: '',
    taxId: '',
    notes: ''
  });

  const [auxFormTab, setAuxFormTab] = useState('identificationContact');
  const auxFormTabs = useMemo(() => ([
    { id: 'identificationContact', label: p('label_name'), icon: User },
    { id: 'fiscal', label: p('label_notes'), icon: FileCheck },
  ]), [p]);

  // Load data on mount
  useEffect(() => {
    loadData();
  }, []);

  // Load auxiliaries when filters change
  useEffect(() => {
    loadAuxiliaries();
  }, [typeFilter, parentFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load parent accounts that support auxiliaries
      const accounts = await getChartOfAccounts();
      const accountsWithAux = accounts.filter(acc => acc.has_auxiliaries);
      setParentAccounts(accountsWithAux);

      // Load auxiliaries
      await loadAuxiliaries();

      // Load stats
      const summaryData = await getAuxiliariesSummary();
      setStats(summaryData.summary);
      
    } catch (err) {
      logger.error('Error loading data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadAuxiliaries = async () => {
    try {
      const filters = {};
      if (typeFilter !== 'all') filters.type = typeFilter;
      if (parentFilter !== 'all') filters.parentAccountNumber = parentFilter;
      if (searchTerm) filters.search = searchTerm;

      const data = await getAuxiliaries(filters);
      setAuxiliaries(sortRecordsNewestFirst(Array.isArray(data) ? data : []));
    } catch (err) {
      logger.error('Error loading auxiliaries:', err);
      setError(err.message);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    // Debounce search
    const timer = setTimeout(() => {
      loadAuxiliaries();
    }, 500);
    return () => clearTimeout(timer);
  };

  const handleGenerateCode = async () => {
    if (!formData.parentAccountNumber) {
      alert(p('alert_select_parent'));
      return;
    }

    try {
      const codeData = await generateAuxiliaryCode(formData.parentAccountNumber);
      setFormData(prev => ({ ...prev, code: codeData.code }));
    } catch (err) {
      logger.error('Error generating code:', err);
      alert(p('alert_generate_error', { message: err.message }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.parentAccountNumber) {
      alert(p('alert_name_parent_required'));
      return;
    }

    try {
      setLoading(true);

      if (editingAux) {
        await updateAuxiliary(editingAux.id, formData);
        notifyUpdated(showNotify, p('entity_label', { defaultValue: 'الحساب المساعد' }));
      } else {
        await createAuxiliary(formData);
        notifyCreated(showNotify, p('entity_label', { defaultValue: 'الحساب المساعد' }));
      }

      resetForm();
      await loadData();
    } catch (err) {
      logger.error('Error saving auxiliary:', err);
      notifyError(showNotify, p('alert_save_error', { message: err.message }));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (aux) => {
    setEditingAux(aux);
    setFormData({
      code: aux.code,
      parentAccountNumber: aux.parent_account_number,
      name: aux.name,
      nameAr: aux.name_ar || '',
      type: aux.type,
      address: aux.address || '',
      phone: aux.phone || '',
      email: aux.email || '',
      taxId: aux.tax_id || '',
      notes: aux.notes || ''
    });
    setShowModal(true);
    setAuxFormTab('identificationContact');
  };

  const handleDelete = async (id) => {
    if (!window.confirm(p('confirm_delete'))) return;

    try {
      setLoading(true);
      await deleteAuxiliary(id);
      notifyDeleted(showNotify, p('entity_label', { defaultValue: 'الحساب المساعد' }));
      await loadData();
    } catch (err) {
      logger.error('Error deleting auxiliary:', err);
      notifyError(showNotify, p('alert_delete_error', { message: err.message }));
    } finally {
      setLoading(false);
    }
  };

  const handleView = (aux) => {
    setViewingAux(aux);
    setShowViewModal(true);
  };

  const resetForm = () => {
    setFormData({
      code: '',
      parentAccountNumber: '',
      name: '',
      nameAr: '',
      type: 'client',
      address: '',
      phone: '',
      email: '',
      taxId: '',
      notes: ''
    });
    setEditingAux(null);
    setShowModal(false);
    setAuxFormTab('identificationContact');
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'client': return <User className="h-4 w-4" />;
      case 'fournisseur': return <Building className="h-4 w-4" />;
      case 'stock': return <Package className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  const getTypeBadgeColor = (type) => {
    switch (type) {
      case 'client': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'fournisseur': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'stock': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const typeLabel = (type) => {
    const map = { client: 'type_client', fournisseur: 'type_supplier', stock: 'type_stock', autre: 'type_other' };
    return map[type] ? p(map[type]) : type;
  };

  const filteredAuxiliaries = auxiliaries.filter(aux => {
    const matchesSearch = searchTerm === '' ||
      aux.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      aux.code?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || aux.type === typeFilter;
    const matchesParent = parentFilter === 'all' || aux.parent_account_number === parentFilter;
    
    return matchesSearch && matchesType && matchesParent;
  });

  const {
    qawaemColumnDefs: auxQawaemColumnDefs,
    handleToggleColumn: handleToggleAuxColumn,
    qawaemVisibleColumnCount: auxQawaemVisibleColumnCount,
    isColVisible: isAuxColVisible,
    visibleDataColumnCount: auxVisibleDataColumnCount } = useComptabiliteTableColumnVisibility(AUXILIARIES_LIST_COLUMNS, p);
  const auxTableColSpan = auxVisibleDataColumnCount + 1;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Users className="h-8 w-8 text-blue-400" />
            {p('title')}
          </h1>
          <p className="text-white/60 mt-2">
            {p('subtitle')}
          </p>
        </div>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => { setAuxFormTab('identificationContact'); setShowModal(true); }}
          className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-blue-500/50 transition-all"
        >
          <Plus className="h-5 w-5" />
          {p('new_auxiliary')}
        </motion.button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20"
          >
            <div className="flex items-center justify-between mb-2">
              <Users className="h-8 w-8 text-blue-400" />
              <span className="text-2xl font-bold text-white">{stats.total_auxiliaries || 0}</span>
            </div>
            <p className="text-white/60 text-sm">{p('stat_total')}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20"
          >
            <div className="flex items-center justify-between mb-2">
              <User className="h-8 w-8 text-blue-400" />
              <span className="text-2xl font-bold text-white">{stats.clients || 0}</span>
            </div>
            <p className="text-white/60 text-sm">{p('stat_clients')}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20"
          >
            <div className="flex items-center justify-between mb-2">
              <Building className="h-8 w-8 text-green-400" />
              <span className="text-2xl font-bold text-white">{stats.fournisseurs || 0}</span>
            </div>
            <p className="text-white/60 text-sm">{p('stat_suppliers')}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20"
          >
            <div className="flex items-center justify-between mb-2">
              <Package className="h-8 w-8 text-purple-400" />
              <span className="text-2xl font-bold text-white">{stats.stocks || 0}</span>
            </div>
            <p className="text-white/60 text-sm">{p('stat_stock')}</p>
          </motion.div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-white/40" />
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearch}
              placeholder={p('search_placeholder')}
              className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
            />
          </div>

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
          >
            <option value="all">{p('filter_all_types')}</option>
            <option value="client">{p('filter_clients')}</option>
            <option value="fournisseur">{p('filter_suppliers')}</option>
            <option value="stock">{p('filter_stock')}</option>
            <option value="autre">{p('filter_other')}</option>
          </select>

          <select
            value={parentFilter}
            onChange={(e) => setParentFilter(e.target.value)}
            className="px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
          >
            <option value="all">{p('filter_all_parents')}</option>
            {parentAccounts.map(acc => (
              <option key={acc.id} value={acc.account_number}>
                {acc.account_number} - {acc.account_name}
              </option>
            ))}
          </select>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={loadData}
            className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-white transition-all"
          >
            <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
            {p('refresh')}
          </motion.button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-500/20 border border-red-500/30 rounded-xl p-4 flex items-center gap-3"
        >
          <AlertCircle className="h-5 w-5 text-red-400" />
          <p className="text-red-400">{error}</p>
        </motion.div>
      )}

      {/* Loading State */}
      {loading && auxiliaries.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-12 w-12 text-blue-400 animate-spin" />
        </div>
      ) : (
        <>
          {/* Auxiliaries Table */}
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 overflow-hidden">
            <div className="glass-table-scroll overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    {isAuxColVisible('code') ? (
                    <th className="text-left p-4 text-white/80 font-semibold">{p('col_code')}</th>
                    ) : null}
                    {isAuxColVisible('name') ? (
                    <th className="text-left p-4 text-white/80 font-semibold">{p('col_name')}</th>
                    ) : null}
                    {isAuxColVisible('type') ? (
                    <th className="text-left p-4 text-white/80 font-semibold">{p('col_type')}</th>
                    ) : null}
                    {isAuxColVisible('parent') ? (
                    <th className="text-left p-4 text-white/80 font-semibold">{p('col_parent')}</th>
                    ) : null}
                    {isAuxColVisible('balance') ? (
                    <th className="text-right p-4 text-white/80 font-semibold">{p('col_balance')}</th>
                    ) : null}
                    {isAuxColVisible('status') ? (
                    <th className="text-center p-4 text-white/80 font-semibold">{p('col_status')}</th>
                    ) : null}
                    <QawaemTableHeaderActionsCell
                      label={p('col_actions')}
                      columns={auxQawaemColumnDefs}
                      onToggle={handleToggleAuxColumn}
                      visibleCount={auxQawaemVisibleColumnCount}
                      totalCount={auxQawaemColumnDefs.length}
                      thClassName="text-right p-4 text-white/80 font-semibold"
                    />
                  </tr>
                </thead>
                <tbody>
                  {filteredAuxiliaries.length === 0 ? (
                    <tr>
                      <td colSpan={auxTableColSpan} className="text-center py-12 text-white/60">
                        <FileText className="h-12 w-12 mx-auto mb-3 text-white/40" />
                        <p>{p('empty_title')}</p>
                        <p className="text-sm mt-1">{p('empty_subtitle')}</p>
                      </td>
                    </tr>
                  ) : (
                    filteredAuxiliaries.map((aux, index) => (
                      <tr
                        key={aux.id}
                        className="border-b border-white/5 hover:bg-white/5 transition-colors"
                      >
                        {isAuxColVisible('code') ? (
                        <td className="p-4">
                          <span className="font-mono text-blue-400 font-semibold">{aux.code}</span>
                        </td>
                        ) : null}
                        {isAuxColVisible('name') ? (
                        <td className="p-4">
                          <div>
                            <p className="text-white font-medium">{aux.name}</p>
                            {aux.name_ar && (
                              <p className="text-white/40 text-sm mt-1">{aux.name_ar}</p>
                            )}
                          </div>
                        </td>
                        ) : null}
                        {isAuxColVisible('type') ? (
                        <td className="p-4">
                          <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border font-medium text-sm ${getTypeBadgeColor(aux.type)}`}>
                            {getTypeIcon(aux.type)}
                            {typeLabel(aux.type)}
                          </span>
                        </td>
                        ) : null}
                        {isAuxColVisible('parent') ? (
                        <td className="p-4">
                          <span className="text-white/70 font-mono text-sm">
                            {aux.parent_account_number}
                          </span>
                        </td>
                        ) : null}
                        {isAuxColVisible('balance') ? (
                        <td className="p-4 text-right">
                          <span className={`font-semibold ${aux.balance >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {formatBrandMoney(aux.balance || 0)}
                          </span>
                        </td>
                        ) : null}
                        {isAuxColVisible('status') ? (
                        <td className="p-4 text-center">
                          {aux.is_active ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-500/20 text-green-400 rounded-lg text-xs font-medium">
                              <CheckCircle className="h-3 w-3" />
                              {p('status_active')}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-500/20 text-gray-400 rounded-lg text-xs font-medium">
                              {p('status_inactive')}
                            </span>
                          )}
                        </td>
                        ) : null}
                        <td className="p-4">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleView(aux)}
                              className="p-2 hover:bg-white/10 rounded-lg text-white/70 hover:text-white transition-colors"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleEdit(aux)}
                              className="p-2 hover:bg-white/10 rounded-lg text-white/70 hover:text-blue-400 transition-colors"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(aux.id)}
                              className="p-2 hover:bg-white/10 rounded-lg text-white/70 hover:text-red-400 transition-colors"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Create/Edit Modal */}
      {showModal && ReactDOM.createPortal(
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4"
          onClick={resetForm}
          role="presentation"
        >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl max-h-[90vh] flex flex-col overflow-hidden"
              style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }}
            >
              <div className="flex items-center justify-between p-6 border-b border-white/10 shrink-0">
                <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                  <Users className="h-6 w-6 text-blue-400" />
                  {editingAux ? p('modal_edit') : p('modal_new')}
                </h2>
                <div className="flex items-center gap-3">
                  <button type="submit" form="aux-form" disabled={loading} className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30 disabled:opacity-50">
                    {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                    <CheckCircle size={18} />
                    {editingAux ? p('btn_update') : p('btn_create')}
                  </button>
                  <button type="button" onClick={resetForm} className="text-white/70 hover:text-white transition-colors"><X size={24} /></button>
                </div>
              </div>

              <div className="shrink-0 px-6 pt-3 border-b border-white/10">
                <div className="flex flex-wrap gap-1" role="tablist">
                  {auxFormTabs.map((tab) => {
                    const TabIcon = tab.icon;
                    const active = auxFormTab === tab.id;
                    return (
                      <button key={tab.id} type="button" role="tab" aria-selected={active} onClick={() => setAuxFormTab(tab.id)} className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-t-xl text-sm font-semibold transition-colors ${active ? 'bg-white/10 text-white border-b-2 border-blue-400 -mb-px' : 'text-white/55 hover:text-white hover:bg-white/5'}`}>
                        <TabIcon size={16} />
                        <span>{tab.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="overflow-y-auto p-6 flex-1 min-h-0">
                <form id="aux-form" onSubmit={handleSubmit}>
                  {auxFormTab === 'identificationContact' && (
                    <div className="space-y-6">
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_parent')}</label>
                          <select required value={formData.parentAccountNumber} onChange={(e) => setFormData({ ...formData, parentAccountNumber: e.target.value, code: '' })} className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                            <option value="">{p('select_account')}</option>
                            {parentAccounts.map(acc => (
                              <option key={acc.id} value={acc.account_number}>{acc.account_number} - {acc.account_name}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_aux_code')}</label>
                          <div className="flex gap-2">
                            <input type="text" value={formData.code} onChange={(e) => setFormData({ ...formData, code: e.target.value })} placeholder={p('placeholder_auto_code')} className="flex-1 px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono" />
                            <button type="button" onClick={handleGenerateCode} disabled={!formData.parentAccountNumber} className="px-4 py-2.5 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30 rounded-xl text-blue-400 font-medium transition-all disabled:opacity-50">{p('auto')}</button>
                          </div>
                        </div>
                        <div>
                          <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_type')}</label>
                          <select required value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })} className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                            <option value="client">{p('type_client')}</option>
                            <option value="fournisseur">{p('type_supplier')}</option>
                            <option value="stock">{p('type_stock')}</option>
                            <option value="autre">{p('type_other')}</option>
                          </select>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_name')} <span className="qawaem-required-mark">*</span></label>
                          <input type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder={p('placeholder_name')} className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        </div>
                        <div>
                          <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_name_ar')}</label>
                          <input type="text" value={formData.nameAr} onChange={(e) => setFormData({ ...formData, nameAr: e.target.value })} placeholder={p('placeholder_name_ar')} className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500" dir="rtl" />
                        </div>
                      </div>
                    </div>
                    <div className="pt-6 border-t border-white/10">
                      <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right flex items-center gap-2"><Phone className="h-4 w-4" />{p('label_phone')}</label>
                          <input type="text" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder={p('placeholder_phone')} className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        </div>
                        <div>
                          <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right flex items-center gap-2"><Mail className="h-4 w-4" />{p('label_email')}</label>
                          <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder={p('placeholder_email')} className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        </div>
                        <div>
                          <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right flex items-center gap-2"><MapPin className="h-4 w-4" />{p('label_address')}</label>
                          <input type="text" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} placeholder={getDefaultLocationPlaceholder()} className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        </div>
                      </div>
                      </div>
                    </div>
                  </div>
                  )}
                  {auxFormTab === 'fiscal' && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right flex items-center gap-2"><FileCheck className="h-4 w-4" />{p('label_tax_id')}</label>
                          <input type="text" value={formData.taxId} onChange={(e) => setFormData({ ...formData, taxId: e.target.value })} placeholder={p('placeholder_tax_id')} className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_notes')}</label>
                          <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={3} placeholder={p('placeholder_notes')} className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                        </div>
                      </div>
                    </div>
                  )}
                </form>
              </div>
            </motion.div>
        </div>,
        document.body
      )}

      {/* View Modal */}
      {showViewModal && viewingAux && ReactDOM.createPortal(
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4"
          onClick={() => setShowViewModal(false)}
          role="presentation"
        >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl border border-white/20 p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                  {getTypeIcon(viewingAux.type)}
                  {p('view_title')}
                </h2>
                <button
                  onClick={() => setShowViewModal(false)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5 text-white/70" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-white/5 rounded-xl">
                  <div>
                    <p className="text-white/60 text-sm mb-1">{p('col_code')}</p>
                    <p className="text-white font-mono font-semibold text-lg">{viewingAux.code}</p>
                  </div>
                  <div>
                    <p className="text-white/60 text-sm mb-1">{p('col_type')}</p>
                    <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border font-medium text-sm ${getTypeBadgeColor(viewingAux.type)}`}>
                      {getTypeIcon(viewingAux.type)}
                      {typeLabel(viewingAux.type)}
                    </span>
                  </div>
                </div>

                <div className="p-4 bg-white/5 rounded-xl">
                  <p className="text-white/60 text-sm mb-1">{p('col_name')}</p>
                  <p className="text-white font-semibold text-lg">{viewingAux.name}</p>
                  {viewingAux.name_ar && (
                    <p className="text-white/60 text-sm mt-1" dir="rtl">{viewingAux.name_ar}</p>
                  )}
                </div>

                <div className="p-4 bg-white/5 rounded-xl">
                  <p className="text-white/60 text-sm mb-1">{p('col_parent')}</p>
                  <p className="text-white font-mono">{viewingAux.parent_account_number}</p>
                </div>

                <div className="grid grid-cols-3 gap-4 p-4 bg-white/5 rounded-xl">
                  <div>
                    <p className="text-white/60 text-sm mb-1">{p('view_debit_total')}</p>
                    <p className="text-blue-400 font-semibold">{formatBrandMoney(viewingAux.total_debit || 0)}</p>
                  </div>
                  <div>
                    <p className="text-white/60 text-sm mb-1">{p('view_credit_total')}</p>
                    <p className="text-green-400 font-semibold">{formatBrandMoney(viewingAux.total_credit || 0)}</p>
                  </div>
                  <div>
                    <p className="text-white/60 text-sm mb-1">{p('col_balance')}</p>
                    <p className={`font-semibold ${viewingAux.balance >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {formatBrandMoney(viewingAux.balance || 0)}
                    </p>
                  </div>
                </div>

                {(viewingAux.phone || viewingAux.email || viewingAux.address) && (
                  <div className="p-4 bg-white/5 rounded-xl space-y-2">
                    <p className="text-white/80 font-semibold mb-3">{p('view_contact')}</p>
                    {viewingAux.phone && (
                      <div className="flex items-center gap-2 text-white/70">
                        <Phone className="h-4 w-4 text-white/40" />
                        <span>{viewingAux.phone}</span>
                      </div>
                    )}
                    {viewingAux.email && (
                      <div className="flex items-center gap-2 text-white/70">
                        <Mail className="h-4 w-4 text-white/40" />
                        <span>{viewingAux.email}</span>
                      </div>
                    )}
                    {viewingAux.address && (
                      <div className="flex items-center gap-2 text-white/70">
                        <MapPin className="h-4 w-4 text-white/40" />
                        <span>{viewingAux.address}</span>
                      </div>
                    )}
                  </div>
                )}

                {viewingAux.tax_id && (
                  <div className="p-4 bg-white/5 rounded-xl">
                    <p className="text-white/60 text-sm mb-1">{p('label_tax_id')}</p>
                    <p className="text-white font-mono">{viewingAux.tax_id}</p>
                  </div>
                )}

                {viewingAux.notes && (
                  <div className="p-4 bg-white/5 rounded-xl">
                    <p className="text-white/60 text-sm mb-1">{p('label_notes')}</p>
                    <p className="text-white/80">{viewingAux.notes}</p>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-white/10">
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    handleEdit(viewingAux);
                  }}
                  className="px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30 rounded-xl text-blue-400 font-medium transition-all"
                >
                  {p('edit')}
                </button>
                <button
                  onClick={() => setShowViewModal(false)}
                  className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-white/70 font-medium transition-all"
                >
                  {p('close')}
                </button>
              </div>
            </motion.div>
        </div>,
        document.body
      )}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </div>
  );
};

export const OperationsAndCodesPage = () => {
  const { t } = useTranslation();
  const { settings } = useSettings();
  const p = (key, opts) => t(`modules.comptabilite.pages.operationsAndCodesPage.${key}`, opts);
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const categoryLabel = (cat) => {
    const map = { Transport: 'category_transport', Entreposage: 'category_storage', Emballage: 'category_packaging', Manutention: 'category_handling' };
    return map[cat] ? p(map[cat]) : cat;
  };


  const [operations, setOperations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [currentView, setCurrentView] = useState('all');
  const [quickFilter, setQuickFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOperation, setSelectedOperation] = useState(null);
  const [formData, setFormData] = useState({
    operation_name: '',
    assigned_code: '',
    code_name: '',
    category: 'Transport',
    priority: 'normal',
    frequency: 0,
    average_duration: '',
    cost: 0,
    status: 'active'
  });

  const [operationFormTab, setOperationFormTab] = useState('generalCodes');
  const operationFormTabs = useMemo(() => ([
    { id: 'generalCodes', label: p('label_operation_name'), icon: Settings },
    { id: 'metrics', label: p('label_cost'), icon: DollarSign },
  ]), [p]);

  useEffect(() => {
    loadOperations();
  }, []);

  const loadOperations = async () => {
    try {
      setLoading(true);
      const data = await getOperations();
      setOperations(sortRecordsNewestFirst(Array.isArray(data) ? data : []));
    } catch (error) {
      logger.error('Error loading operations:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateOperationReference = () => {
    const prefix = getEntityPrefix(settings, 'operationCode', 'OPR');
    return generateEntityReference(prefix, operations, (row) => row.assigned_code || row.code);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const assignedCode = (formData.assigned_code || '').trim() || generateOperationReference();
      const payload = {
        ...formData,
        assigned_code: assignedCode,
        code: assignedCode,
        frequency: parseInt(formData.frequency) || 0,
        cost: parseFloat(formData.cost) || 0
      };
      if (selectedOperation) {
        await updateOperation(selectedOperation.id, payload);
        notifyUpdated(showNotify, p('entity_label', { defaultValue: 'العملية' }));
      } else {
        await createOperation(payload);
        notifyCreated(showNotify, p('entity_label', { defaultValue: 'العملية' }));
      }
      await loadOperations();
      resetForm();
    } catch (error) {
      logger.error('Error saving operation:', error);
      notifyError(showNotify, p('alert_save_error'));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (operation) => {
    setSelectedOperation(operation);
    setFormData(operation);
    setShowModal(true);
    setOperationFormTab('generalCodes');
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('confirm_delete'))) {
      try {
        setLoading(true);
        await deleteOperation(id);
        notifyDeleted(showNotify, p('entity_label', { defaultValue: 'العملية' }));
        await loadOperations();
      } catch (error) {
        logger.error('Error deleting operation:', error);
        notifyError(showNotify, p('alert_delete_error'));
      } finally {
        setLoading(false);
      }
    }
  };

  const handleView = (operation) => {
    setSelectedOperation(operation);
    setShowViewModal(true);
  };

  const resetForm = () => {
    setFormData({ operation_name: '', assigned_code: '', code_name: '', category: 'Transport', priority: 'normal', frequency: 0, average_duration: '', cost: 0, status: 'active' });
    setSelectedOperation(null);
    setShowModal(false);
    setOperationFormTab('generalCodes');
  };

  const openNewOperationModal = () => {
    const nextCode = generateOperationReference();
    setSelectedOperation(null);
    setFormData({
      operation_name: '',
      assigned_code: nextCode,
      code_name: '',
      category: 'Transport',
      priority: 'normal',
      frequency: 0,
      average_duration: '',
      cost: 0,
      status: 'active',
    });
    setOperationFormTab('generalCodes');
    setShowModal(true);
  };

  const stats = [
    { labelKey: 'stat_total', value: operations.length, icon: Settings, color: 'from-blue-500 to-blue-600' },
    { labelKey: 'stat_active', value: operations.filter(o => o.status === 'active').length, icon: Activity, color: 'from-green-500 to-green-600' },
    { labelKey: 'stat_frequency', value: operations.reduce((sum, o) => sum + (o.frequency || 0), 0), icon: TrendingUp, color: 'from-purple-500 to-purple-600' },
    { labelKey: 'stat_avg_cost', value: operations.length > 0 ? formatBrandMoney(Math.round(operations.reduce((sum, o) => sum + (o.cost || 0), 0) / operations.length)) : formatBrandMoney(0), icon: DollarSign, color: 'from-amber-500 to-amber-600' }
  ];

  const filteredOperations = operations.filter(operation => {
    const matchesView = currentView === 'all' || operation.status === currentView;
    const matchesFilter = quickFilter === 'all' || operation.category === quickFilter;
    const matchesSearch = !searchTerm || 
      operation.operation_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      operation.assigned_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      operation.code_name?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesView && matchesFilter && matchesSearch;
  });

  const {
    qawaemColumnDefs: operationsQawaemColumnDefs,
    handleToggleColumn: handleToggleOperationsColumn,
    qawaemVisibleColumnCount: operationsQawaemVisibleColumnCount,
    isColVisible: isOperationsColVisible } = useComptabiliteTableColumnVisibility(OPERATIONS_LIST_COLUMNS, p);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Settings className="h-8 w-8" />
            {p('title')}
          </h1>
          <p className="text-white/60 mt-2">{p('subtitle')}</p>
        </div>
        <button
          onClick={openNewOperationModal}
          className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-300 flex items-center space-x-2"
        >
          <Plus className="h-5 w-5" />
          <span>{p('new_operation')}</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.slice(0, 4).map((stat, index) => (
          <motion.div
            key={stat.labelKey || stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/60 text-sm font-medium">{stat.labelKey ? p(stat.labelKey) : stat.label}</p>
                <p className="text-2xl font-bold text-white mt-1">{stat.value}</p>
              </div>
              <div className={`p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}>
                <stat.icon className="h-6 w-6 text-white" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Main Content */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        {/* Tabs */}
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20 mb-6 inline-flex">
          {['all', 'active', 'inactive'].map(view => (
            <button
              key={view}
              onClick={() => setCurrentView(view)}
              className={`px-6 py-2 rounded-xl font-medium transition-all ${
                currentView === view
                  ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white'
                  : 'text-white/60 hover:text-white'
              }`}
            >
              {view === 'all' ? p('tab_all') : view === 'active' ? p('tab_active') : p('tab_inactive')}
            </button>
          ))}
        </div>

        {/* Quick Filters */}
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-4 border border-white/20 mb-6">
          <div className="flex flex-wrap gap-2">
            {['all', 'Transport', 'Entreposage', 'Emballage', 'Manutention'].map(filter => (
              <button
                key={filter}
                onClick={() => setQuickFilter(filter)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                  quickFilter === filter
                    ? 'bg-blue-600 text-white'
                    : 'bg-white/5 text-white/60 hover:bg-white/10'
                }`}
              >
                {filter === 'all' ? p('filter_all_categories') : categoryLabel(filter)}
              </button>
            ))}
          </div>
        </div>

        {/* Search and Export */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/40 h-5 w-5" />
            <input
              type="text"
              placeholder={p('search_placeholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex gap-2">
            <button className="bg-white/5 hover:bg-white/10 text-white px-6 py-3 rounded-xl font-medium transition-all flex items-center space-x-2 border border-white/10">
              <FileDown className="h-5 w-5" />
              <span>{p('export_pdf')}</span>
            </button>
            <button className="bg-white/5 hover:bg-white/10 text-white px-6 py-3 rounded-xl font-medium transition-all flex items-center space-x-2 border border-white/10">
              <FileDown className="h-5 w-5" />
              <span>{p('export_excel')}</span>
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="glass-table-scroll overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                {isOperationsColVisible('operation') ? (
                <th className="text-left py-4 px-4 text-white/60 font-medium">{p('col_operation')}</th>
                ) : null}
                {isOperationsColVisible('code') ? (
                <th className="text-left py-4 px-4 text-white/60 font-medium">{p('col_code')}</th>
                ) : null}
                {isOperationsColVisible('code_name') ? (
                <th className="text-left py-4 px-4 text-white/60 font-medium">{p('col_code_name')}</th>
                ) : null}
                {isOperationsColVisible('category') ? (
                <th className="text-left py-4 px-4 text-white/60 font-medium">{p('col_category')}</th>
                ) : null}
                {isOperationsColVisible('frequency') ? (
                <th className="text-left py-4 px-4 text-white/60 font-medium">{p('col_frequency')}</th>
                ) : null}
                {isOperationsColVisible('cost') ? (
                <th className="text-left py-4 px-4 text-white/60 font-medium">{p('col_cost')}</th>
                ) : null}
                {isOperationsColVisible('priority') ? (
                <th className="text-left py-4 px-4 text-white/60 font-medium">{p('col_priority')}</th>
                ) : null}
                <QawaemTableHeaderActionsCell
                  label={p('col_actions')}
                  columns={operationsQawaemColumnDefs}
                  onToggle={handleToggleOperationsColumn}
                  visibleCount={operationsQawaemVisibleColumnCount}
                  totalCount={operationsQawaemColumnDefs.length}
                  thClassName="text-right py-4 px-4 text-white/60 font-medium"
                />
              </tr>
            </thead>
            <tbody>
              {filteredOperations.map((operation) => (
                <tr key={operation.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                  {isOperationsColVisible('operation') ? (
                  <td className="py-4 px-4 text-white font-medium">{operation.operation_name}</td>
                  ) : null}
                  {isOperationsColVisible('code') ? (
                  <td className="py-4 px-4 text-white/80 font-mono">{operation.assigned_code}</td>
                  ) : null}
                  {isOperationsColVisible('code_name') ? (
                  <td className="py-4 px-4 text-white/80">{operation.code_name}</td>
                  ) : null}
                  {isOperationsColVisible('category') ? (
                  <td className="py-4 px-4 text-white/80">{categoryLabel(operation.category)}</td>
                  ) : null}
                  {isOperationsColVisible('frequency') ? (
                  <td className="py-4 px-4 text-white/80">{p('frequency_per_month', { count: operation.frequency })}</td>
                  ) : null}
                  {isOperationsColVisible('cost') ? (
                  <td className="py-4 px-4 text-white font-medium">{operation.cost}</td>
                  ) : null}
                  {isOperationsColVisible('priority') ? (
                  <td className="py-4 px-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      operation.priority === 'high' ? 'bg-red-500/20 text-red-300' :
                      operation.priority === 'normal' ? 'bg-blue-500/20 text-blue-300' :
                      'bg-gray-500/20 text-gray-300'
                    }`}>
                      {operation.priority === 'high' ? p('priority_high') : operation.priority === 'normal' ? p('priority_normal') : p('priority_low')}
                    </span>
                  </td>
                  ) : null}
                  <td className="py-4 px-4">
                    <div className="flex items-center justify-end space-x-2">
                      <button onClick={() => handleView(operation)} className="p-2 hover:bg-white/10 rounded-lg transition-colors" title={p('view')}>
                        <Eye className="h-4 w-4 text-blue-400" />
                      </button>
                      <button onClick={() => handleEdit(operation)} className="p-2 hover:bg-white/10 rounded-lg transition-colors" title={p('edit')}>
                        <Edit className="h-4 w-4 text-amber-400" />
                      </button>
                      <button onClick={() => handleDelete(operation.id)} className="p-2 hover:bg-white/10 rounded-lg transition-colors" title={p('delete')}>
                        <Trash2 className="h-4 w-4 text-red-400" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={resetForm} role="presentation">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} onClick={(e) => e.stopPropagation()} className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl max-h-[90vh] flex flex-col overflow-hidden" style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }}>
            <div className="flex items-center justify-between p-6 border-b border-white/10 shrink-0">
              <h2 className="text-2xl font-bold text-white">{selectedOperation ? p('modal_edit') : p('modal_new')}</h2>
              <div className="flex items-center gap-3">
                <button type="submit" form="operation-form" className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"><CheckCircle size={18} />{selectedOperation ? p('btn_update') : p('btn_create')}</button>
                <button type="button" onClick={resetForm} className="text-white/70 hover:text-white transition-colors"><X size={24} /></button>
              </div>
            </div>
            <div className="shrink-0 px-6 pt-3 border-b border-white/10">
              <div className="flex flex-wrap gap-1" role="tablist">
                {operationFormTabs.map((tab) => {
                  const TabIcon = tab.icon;
                  const active = operationFormTab === tab.id;
                  return (
                    <button key={tab.id} type="button" role="tab" aria-selected={active} onClick={() => setOperationFormTab(tab.id)} className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-t-xl text-sm font-semibold transition-colors ${active ? 'bg-white/10 text-white border-b-2 border-blue-400 -mb-px' : 'text-white/55 hover:text-white hover:bg-white/5'}`}>
                      <TabIcon size={16} />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="operation-form" onSubmit={handleSubmit}>
                {operationFormTab === 'generalCodes' && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_operation_name')} <span className="qawaem-required-mark">*</span></label>
                        <input type="text" required value={formData.operation_name} onChange={(e) => setFormData({ ...formData, operation_name: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      </div>
                      <div>
                        <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_status')}</label>
                        <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                          <option value="active">{p('status_active')}</option>
                          <option value="inactive">{p('status_inactive')}</option>
                        </select>
                      </div>
                    </div>
                    <div className="pt-6 border-t border-white/10">
                      <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_assigned_code')} <span className="qawaem-required-mark">*</span></label>
                        <input type="text" required value={formData.assigned_code} onChange={(e) => setFormData({ ...formData, assigned_code: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      </div>
                      <div>
                        <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_code_name')} <span className="qawaem-required-mark">*</span></label>
                        <input type="text" required value={formData.code_name} onChange={(e) => setFormData({ ...formData, code_name: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      </div>
                      <div>
                        <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_category')}</label>
                        <select value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                          <option value="Transport">{p('category_transport')}</option>
                          <option value="Entreposage">{p('category_storage')}</option>
                          <option value="Emballage">{p('category_packaging')}</option>
                          <option value="Manutention">{p('category_handling')}</option>
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_priority')}</label>
                        <select value={formData.priority} onChange={(e) => setFormData({ ...formData, priority: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                          <option value="high">{p('priority_high')}</option>
                          <option value="normal">{p('priority_normal')}</option>
                          <option value="low">{p('priority_low')}</option>
                        </select>
                      </div>
                    </div>
                      </div>
                    </div>
                  </div>
                )}
                {operationFormTab === 'metrics' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_frequency')} <span className="qawaem-required-mark">*</span></label>
                      <input type="number" required value={formData.frequency} onChange={(e) => setFormData({ ...formData, frequency: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                      <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_avg_duration')}</label>
                      <input type="text" value={formData.average_duration} onChange={(e) => setFormData({ ...formData, average_duration: e.target.value })} placeholder={p('placeholder_duration')} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                      <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_cost')} <span className="qawaem-required-mark">*</span></label>
                      <input type="number" step="0.01" required value={formData.cost} onChange={(e) => setFormData({ ...formData, cost: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                  </div>
                )}
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      {/* View Modal */}
      {showViewModal && selectedOperation && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={() => setShowViewModal(false)} role="presentation">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white/5 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl p-8 max-h-[90vh] overflow-y-auto shadow-[0_0_40px_rgba(59,130,246,0.4)]"
              style={{ width: '90%', maxWidth: '700px' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">{p('view_title')}</h2>
                <button onClick={() => setShowViewModal(false)} className="text-white/60 hover:text-white">
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <p className="text-white/60 text-sm mb-1">{p('col_operation')}</p>
                  <p className="text-white font-medium">{selectedOperation.operationName}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-white/60 text-sm mb-1">{p('label_assigned_code')}</p>
                    <p className="text-white font-medium font-mono">{selectedOperation.assignedCode}</p>
                  </div>
                  <div>
                    <p className="text-white/60 text-sm mb-1">{p('label_code_name')}</p>
                    <p className="text-white font-medium">{selectedOperation.codeName}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-white/60 text-sm mb-1">{p('col_category')}</p>
                    <p className="text-white font-medium">{categoryLabel(selectedOperation.category)}</p>
                  </div>
                  <div>
                    <p className="text-white/60 text-sm mb-1">{p('col_priority')}</p>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      selectedOperation.priority === 'high' ? 'bg-red-500/20 text-red-300' :
                      selectedOperation.priority === 'normal' ? 'bg-blue-500/20 text-blue-300' :
                      'bg-gray-500/20 text-gray-300'
                    }`}>
                      {selectedOperation.priority === 'high' ? p('priority_high') : selectedOperation.priority === 'normal' ? p('priority_normal') : p('priority_low')}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-white/60 text-sm mb-1">{p('col_frequency')}</p>
                    <p className="text-white font-medium">{selectedOperation.frequency}/mois</p>
                  </div>
                  <div>
                    <p className="text-white/60 text-sm mb-1">{p('label_avg_duration')}</p>
                    <p className="text-white font-medium">{selectedOperation.averageDuration}</p>
                  </div>
                  <div>
                    <p className="text-white/60 text-sm mb-1">{p('col_cost')}</p>
                    <p className="text-white font-bold">{selectedOperation.cost}</p>
                  </div>
                </div>

                {selectedOperation.lastUsed && (
                  <div>
                    <p className="text-white/60 text-sm mb-1">{p('view_last_used')}</p>
                    <p className="text-white font-medium">{new Date(selectedOperation.lastUsed).toLocaleDateString(getBrandLocale())}</p>
                  </div>
                )}
              </div>

              <button
                onClick={() => setShowViewModal(false)}
                className="w-full mt-6 bg-white/5 hover:bg-white/10 text-white py-3 rounded-xl font-semibold transition-all border border-white/10"
              >
                {p('close')}
              </button>
            </motion.div>
        </div>,
        document.body
      )}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </div>
  );
};

export const CodesPage = () => {
  const { t } = useTranslation();
  const { settings } = useSettings();
  const p = (key, opts) => t(`modules.comptabilite.pages.codesPage.${key}`, opts);
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const categoryLabel = (cat) => {
    const map = { Transport: 'category_transport', Entreposage: 'category_storage', Emballage: 'category_packaging', Manutention: 'category_handling' };
    return map[cat] ? p(map[cat]) : cat;
  };


  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [currentView, setCurrentView] = useState('all');
  const [quickFilter, setQuickFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCode, setSelectedCode] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    category: 'Transport',
    status: 'active',
    priority: 'normal'
  });


  useEffect(() => {
    loadCodes();
  }, []);

  const loadCodes = async () => {
    try {
      setLoading(true);
      const data = await getCodes();
      setCodes(sortRecordsNewestFirst(Array.isArray(data) ? data : []));
    } catch (error) {
      logger.error('Error loading codes:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateCodeReference = () => {
    const prefix = getEntityPrefix(settings, 'comptabiliteCode', 'COD');
    return generateEntityReference(prefix, codes, (row) => row.code);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const payload = {
        ...formData,
        code: (formData.code || '').trim() || generateCodeReference(),
      };
      if (selectedCode) {
        await updateCode(selectedCode.id, payload);
        notifyUpdated(showNotify, p('entity_label', { defaultValue: 'الرمز' }));
      } else {
        await createCode(payload);
        notifyCreated(showNotify, p('entity_label', { defaultValue: 'الرمز' }));
      }
      await loadCodes();
      resetForm();
    } catch (error) {
      logger.error('Error saving code:', error);
      notifyError(showNotify, p('alert_save_error'));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (code) => {
    setSelectedCode(code);
    setFormData(code);
    setShowModal(true);
      };

  const handleDelete = async (id) => {
    if (window.confirm(p('confirm_delete'))) {
      try {
        setLoading(true);
        await deleteCode(id);
        notifyDeleted(showNotify, p('entity_label', { defaultValue: 'الرمز' }));
        await loadCodes();
      } catch (error) {
        logger.error('Error deleting code:', error);
        notifyError(showNotify, p('alert_delete_error'));
      } finally {
        setLoading(false);
      }
    }
  };

  const handleView = (code) => {
    setSelectedCode(code);
    setShowViewModal(true);
  };

  const resetForm = () => {
    setFormData({ code: '', name: '', description: '', category: 'Transport', status: 'active', priority: 'normal' });
    setSelectedCode(null);
    setShowModal(false);
      };

  const openNewCodeModal = () => {
    setSelectedCode(null);
    setFormData({
      code: generateCodeReference(),
      name: '',
      description: '',
      category: 'Transport',
      status: 'active',
      priority: 'normal',
    });
    setShowModal(true);
  };

  const stats = [
    { labelKey: 'stat_total', value: codes.length, icon: FileText, color: 'from-blue-500 to-blue-600' },
    { labelKey: 'stat_active', value: codes.filter(c => c.status === 'active').length, icon: CheckCircle, color: 'from-green-500 to-green-600' },
    { labelKey: 'stat_inactive', value: codes.filter(c => c.status === 'inactive').length, icon: XCircle, color: 'from-red-500 to-red-600' },
    { labelKey: 'stat_categories', value: [...new Set(codes.map(c => c.category))].length, icon: Layers, color: 'from-purple-500 to-purple-600' }
  ];

  const filteredCodes = codes.filter(code => {
    const matchesView = currentView === 'all' || code.status === currentView;
    const matchesFilter = quickFilter === 'all' || code.category === quickFilter;
    const matchesSearch = !searchTerm || 
      code.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      code.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      code.description?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesView && matchesFilter && matchesSearch;
  });

  const {
    qawaemColumnDefs: codesQawaemColumnDefs,
    handleToggleColumn: handleToggleCodesColumn,
    qawaemVisibleColumnCount: codesQawaemVisibleColumnCount,
    isColVisible: isCodesColVisible } = useComptabiliteTableColumnVisibility(CODES_LIST_COLUMNS, p);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <FileText className="h-8 w-8" />
            {p('title')}
          </h1>
          <p className="text-white/60 mt-2">{p('subtitle')}</p>
        </div>
        <button
          onClick={openNewCodeModal}
          className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-300 flex items-center space-x-2"
        >
          <Plus className="h-5 w-5" />
          <span>{p('new_code')}</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.slice(0, 4).map((stat, index) => (
          <motion.div
            key={stat.labelKey || stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/60 text-sm font-medium">{stat.labelKey ? p(stat.labelKey) : stat.label}</p>
                <p className="text-2xl font-bold text-white mt-1">{stat.value}</p>
              </div>
              <div className={`p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}>
                <stat.icon className="h-6 w-6 text-white" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Main Content */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        {/* Tabs */}
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20 mb-6 inline-flex">
          {['all', 'active', 'inactive'].map(view => (
            <button
              key={view}
              onClick={() => setCurrentView(view)}
              className={`px-6 py-2 rounded-xl font-medium transition-all ${
                currentView === view
                  ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white'
                  : 'text-white/60 hover:text-white'
              }`}
            >
              {view === 'all' ? p('tab_all') : view === 'active' ? p('tab_active') : p('tab_inactive')}
            </button>
          ))}
        </div>

        {/* Quick Filters */}
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-4 border border-white/20 mb-6">
          <div className="flex flex-wrap gap-2">
            {['all', 'Transport', 'Entreposage', 'Emballage', 'Manutention'].map(filter => (
              <button
                key={filter}
                onClick={() => setQuickFilter(filter)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                  quickFilter === filter
                    ? 'bg-blue-600 text-white'
                    : 'bg-white/5 text-white/60 hover:bg-white/10'
                }`}
              >
                {filter === 'all' ? p('filter_all_categories') : categoryLabel(filter)}
              </button>
            ))}
          </div>
        </div>

        {/* Search and Export */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/40 h-5 w-5" />
            <input
              type="text"
              placeholder={p('search_placeholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex gap-2">
            <button className="bg-white/5 hover:bg-white/10 text-white px-6 py-3 rounded-xl font-medium transition-all flex items-center space-x-2 border border-white/10">
              <FileDown className="h-5 w-5" />
              <span>{p('export_pdf')}</span>
            </button>
            <button className="bg-white/5 hover:bg-white/10 text-white px-6 py-3 rounded-xl font-medium transition-all flex items-center space-x-2 border border-white/10">
              <FileDown className="h-5 w-5" />
              <span>{p('export_excel')}</span>
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="glass-table-scroll overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                {isCodesColVisible('code') ? (
                <th className="text-left py-4 px-4 text-white/60 font-medium">{p('col_code')}</th>
                ) : null}
                {isCodesColVisible('name') ? (
                <th className="text-left py-4 px-4 text-white/60 font-medium">{p('col_name')}</th>
                ) : null}
                {isCodesColVisible('description') ? (
                <th className="text-left py-4 px-4 text-white/60 font-medium">{p('col_description')}</th>
                ) : null}
                {isCodesColVisible('category') ? (
                <th className="text-left py-4 px-4 text-white/60 font-medium">{p('col_category')}</th>
                ) : null}
                {isCodesColVisible('priority') ? (
                <th className="text-left py-4 px-4 text-white/60 font-medium">{p('col_priority')}</th>
                ) : null}
                {isCodesColVisible('status') ? (
                <th className="text-left py-4 px-4 text-white/60 font-medium">{p('col_status')}</th>
                ) : null}
                <QawaemTableHeaderActionsCell
                  label={p('col_actions')}
                  columns={codesQawaemColumnDefs}
                  onToggle={handleToggleCodesColumn}
                  visibleCount={codesQawaemVisibleColumnCount}
                  totalCount={codesQawaemColumnDefs.length}
                  thClassName="text-right py-4 px-4 text-white/60 font-medium"
                />
              </tr>
            </thead>
            <tbody>
              {filteredCodes.map((code) => (
                <tr key={code.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                  {isCodesColVisible('code') ? (
                  <td className="py-4 px-4 text-white font-medium font-mono">{code.code}</td>
                  ) : null}
                  {isCodesColVisible('name') ? (
                  <td className="py-4 px-4 text-white">{code.name}</td>
                  ) : null}
                  {isCodesColVisible('description') ? (
                  <td className="py-4 px-4 text-white/80">{code.description}</td>
                  ) : null}
                  {isCodesColVisible('category') ? (
                  <td className="py-4 px-4 text-white/80">{categoryLabel(code.category)}</td>
                  ) : null}
                  {isCodesColVisible('priority') ? (
                  <td className="py-4 px-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      code.priority === 'high' ? 'bg-red-500/20 text-red-300' :
                      code.priority === 'normal' ? 'bg-blue-500/20 text-blue-300' :
                      'bg-gray-500/20 text-gray-300'
                    }`}>
                      {code.priority === 'high' ? p('priority_high') : code.priority === 'normal' ? p('priority_normal') : p('priority_low')}
                    </span>
                  </td>
                  ) : null}
                  {isCodesColVisible('status') ? (
                  <td className="py-4 px-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      code.status === 'active' ? 'bg-green-500/20 text-green-300' :
                      'bg-gray-500/20 text-gray-300'
                    }`}>
                      {code.status === 'active' ? p('status_active') : p('status_inactive')}
                    </span>
                  </td>
                  ) : null}
                  <td className="py-4 px-4">
                    <div className="flex items-center justify-end space-x-2">
                      <button onClick={() => handleView(code)} className="p-2 hover:bg-white/10 rounded-lg transition-colors" title={p('view')}>
                        <Eye className="h-4 w-4 text-blue-400" />
                      </button>
                      <button onClick={() => handleEdit(code)} className="p-2 hover:bg-white/10 rounded-lg transition-colors" title={p('edit')}>
                        <Edit className="h-4 w-4 text-amber-400" />
                      </button>
                      <button onClick={() => handleDelete(code.id)} className="p-2 hover:bg-white/10 rounded-lg transition-colors" title={p('delete')}>
                        <Trash2 className="h-4 w-4 text-red-400" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={resetForm} role="presentation">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} onClick={(e) => e.stopPropagation()} className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl max-h-[90vh] flex flex-col overflow-hidden" style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }}>
            <div className="flex items-center justify-between p-6 border-b border-white/10 shrink-0">
              <h2 className="text-2xl font-bold text-white">{selectedCode ? p('modal_edit') : p('modal_new')}</h2>
              <div className="flex items-center gap-3">
                <button type="submit" form="code-form" className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"><CheckCircle size={18} />{selectedCode ? p('btn_update') : p('btn_create')}</button>
                <button type="button" onClick={resetForm} className="text-white/70 hover:text-white transition-colors"><X size={24} /></button>
              </div>
            </div>
            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="code-form" onSubmit={handleSubmit}>
                <div className="space-y-4 pb-6 border-b border-white/10">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_code')} <span className="qawaem-required-mark">*</span></label>
                      <input type="text" required value={formData.code} onChange={(e) => setFormData({ ...formData, code: e.target.value })} placeholder={p('placeholder_code')} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                      <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_name')} <span className="qawaem-required-mark">*</span></label>
                      <input type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                      <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_category')}</label>
                      <select value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                        <option value="Transport">{p('category_transport')}</option>
                        <option value="Entreposage">{p('category_storage')}</option>
                        <option value="Emballage">{p('category_packaging')}</option>
                        <option value="Manutention">{p('category_handling')}</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="md:col-span-3">
                        <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_description')}</label>
                        <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={3} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      </div>
                      <div>
                        <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_priority')}</label>
                        <select value={formData.priority} onChange={(e) => setFormData({ ...formData, priority: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                          <option value="high">{p('priority_high')}</option>
                          <option value="normal">{p('priority_normal')}</option>
                          <option value="low">{p('priority_low')}</option>
                        </select>
                      </div>
                      <div>
                        <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_status')}</label>
                        <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                          <option value="active">{p('status_active')}</option>
                          <option value="inactive">{p('status_inactive')}</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      {/* View Modal */}
      {showViewModal && selectedCode && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={() => setShowViewModal(false)} role="presentation">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white/5 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl p-8 max-h-[90vh] overflow-y-auto shadow-[0_0_40px_rgba(59,130,246,0.4)]"
              style={{ width: '90%', maxWidth: '700px' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">{p('view_title')}</h2>
                <button onClick={() => setShowViewModal(false)} className="text-white/60 hover:text-white">
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-white/60 text-sm mb-1">{p('col_code')}</p>
                    <p className="text-white font-medium font-mono">{selectedCode.code}</p>
                  </div>
                  <div>
                    <p className="text-white/60 text-sm mb-1">{p('col_category')}</p>
                    <p className="text-white font-medium">{categoryLabel(selectedCode.category)}</p>
                  </div>
                </div>

                <div>
                  <p className="text-white/60 text-sm mb-1">{p('col_name')}</p>
                  <p className="text-white font-medium">{selectedCode.name}</p>
                </div>

                <div>
                  <p className="text-white/60 text-sm mb-1">{p('col_description')}</p>
                  <p className="text-white font-medium">{selectedCode.description}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-white/60 text-sm mb-1">{p('col_priority')}</p>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      selectedCode.priority === 'high' ? 'bg-red-500/20 text-red-300' :
                      selectedCode.priority === 'normal' ? 'bg-blue-500/20 text-blue-300' :
                      'bg-gray-500/20 text-gray-300'
                    }`}>
                      {selectedCode.priority === 'high' ? p('priority_high') : selectedCode.priority === 'normal' ? p('priority_normal') : p('priority_low')}
                    </span>
                  </div>
                  <div>
                    <p className="text-white/60 text-sm mb-1">{p('col_status')}</p>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      selectedCode.status === 'active' ? 'bg-green-500/20 text-green-300' :
                      'bg-gray-500/20 text-gray-300'
                    }`}>
                      {selectedCode.status === 'active' ? p('status_active') : p('status_inactive')}
                    </span>
                  </div>
                </div>

                <div>
                  <p className="text-white/60 text-sm mb-1">{p('view_created_date')}</p>
                  <p className="text-white font-medium">{new Date(selectedCode.createdDate).toLocaleDateString(getBrandLocale())}</p>
                </div>
              </div>

              <button
                onClick={() => setShowViewModal(false)}
                className="w-full mt-6 bg-white/5 hover:bg-white/10 text-white py-3 rounded-xl font-semibold transition-all border border-white/10"
              >
                {p('close')}
              </button>
            </motion.div>
        </div>,
        document.body
      )}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </div>
  );
};

export const CodesAffectationPage = () => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.comptabilite.pages.codesAffectationPage.${key}`, opts);
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const categoryLabel = (cat) => {
    const map = { Transport: 'category_transport', Entreposage: 'category_storage', Emballage: 'category_packaging', Manutention: 'category_handling' };
    return map[cat] ? p(map[cat]) : cat;
  };


  const [affectations, setAffectations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [currentView, setCurrentView] = useState('all');
  const [quickFilter, setQuickFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAffectation, setSelectedAffectation] = useState(null);
  const [formData, setFormData] = useState({
    operation_name: '',
    code: '',
    operation_type: 'Transport',
    affectation_date: new Date().toISOString().split('T')[0],
    responsible: '',
    status: 'active',
    notes: ''
  });


  useEffect(() => {
    loadAffectations();
  }, []);

  const loadAffectations = async () => {
    try {
      setLoading(true);
      const data = await getAffectations();
      setAffectations(sortRecordsNewestFirst(Array.isArray(data) ? data : []));
    } catch (error) {
      logger.error('Error loading affectations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      if (selectedAffectation) {
        await updateAffectation(selectedAffectation.id, formData);
        notifyUpdated(showNotify, p('entity_label', { defaultValue: 'التخصيص' }));
      } else {
        await createAffectation(formData);
        notifyCreated(showNotify, p('entity_label', { defaultValue: 'التخصيص' }));
      }
      await loadAffectations();
      resetForm();
    } catch (error) {
      logger.error('Error saving affectation:', error);
      notifyError(showNotify, p('alert_save_error'));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (affectation) => {
    setSelectedAffectation(affectation);
    setFormData(affectation);
    setShowModal(true);
      };

  const handleDelete = async (id) => {
    if (window.confirm(p('confirm_delete'))) {
      try {
        setLoading(true);
        await deleteAffectation(id);
        notifyDeleted(showNotify, p('entity_label', { defaultValue: 'التخصيص' }));
        await loadAffectations();
      } catch (error) {
        logger.error('Error deleting affectation:', error);
        notifyError(showNotify, p('alert_delete_error'));
      } finally {
        setLoading(false);
      }
    }
  };

  const handleView = (affectation) => {
    setSelectedAffectation(affectation);
    setShowViewModal(true);
  };

  const resetForm = () => {
    setFormData({ operation_name: '', code: '', operation_type: 'Transport', affectation_date: new Date().toISOString().split('T')[0], responsible: '', status: 'active', notes: '' });
    setSelectedAffectation(null);
    setShowModal(false);
      };

  const stats = [
    { labelKey: 'stat_total', value: affectations.length, icon: Link, color: 'from-blue-500 to-blue-600' },
    { labelKey: 'stat_active', value: affectations.filter(a => a.status === 'active').length, icon: CheckCircle, color: 'from-green-500 to-green-600' },
    { labelKey: 'stat_pending', value: affectations.filter(a => a.status === 'pending').length, icon: AlertCircle, color: 'from-amber-500 to-amber-600' },
    { labelKey: 'stat_this_month', value: affectations.filter(a => new Date(a.affectationDate).getMonth() === new Date().getMonth()).length, icon: TrendingUp, color: 'from-purple-500 to-purple-600' }
  ];

  const filteredAffectations = affectations.filter(affectation => {
    const matchesView = currentView === 'all' || affectation.status === currentView;
    const matchesFilter = quickFilter === 'all' || affectation.operation_type === quickFilter;
    const matchesSearch = !searchTerm || 
      affectation.operation_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      affectation.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      affectation.responsible?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesView && matchesFilter && matchesSearch;
  });

  const {
    qawaemColumnDefs: affectationsQawaemColumnDefs,
    handleToggleColumn: handleToggleAffectationsColumn,
    qawaemVisibleColumnCount: affectationsQawaemVisibleColumnCount,
    isColVisible: isAffectationsColVisible } = useComptabiliteTableColumnVisibility(AFFECTATIONS_LIST_COLUMNS, p);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Link className="h-8 w-8" />
            {p('title')}
          </h1>
          <p className="text-white/60 mt-2">{p('subtitle')}</p>
        </div>
        <button
          onClick={() => { setAffectationFormTab('operation'); setShowModal(true); }}
          className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-300 flex items-center space-x-2"
        >
          <Plus className="h-5 w-5" />
          <span>{p('new_affectation')}</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.slice(0, 4).map((stat, index) => (
          <motion.div
            key={stat.labelKey || stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/60 text-sm font-medium">{stat.labelKey ? p(stat.labelKey) : stat.label}</p>
                <p className="text-2xl font-bold text-white mt-1">{stat.value}</p>
              </div>
              <div className={`p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}>
                <stat.icon className="h-6 w-6 text-white" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Main Content */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        {/* Tabs */}
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20 mb-6 inline-flex">
          {['all', 'active', 'pending', 'inactive'].map(view => (
            <button
              key={view}
              onClick={() => setCurrentView(view)}
              className={`px-6 py-2 rounded-xl font-medium transition-all ${
                currentView === view
                  ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white'
                  : 'text-white/60 hover:text-white'
              }`}
            >
              {view === 'all' ? p('tab_all') : view === 'active' ? p('tab_active') : view === 'pending' ? p('tab_pending') : p('tab_inactive')}
            </button>
          ))}
        </div>

        {/* Quick Filters */}
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-4 border border-white/20 mb-6">
          <div className="flex flex-wrap gap-2">
            {['all', 'Transport', 'Entreposage', 'Emballage', 'Manutention'].map(filter => (
              <button
                key={filter}
                onClick={() => setQuickFilter(filter)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                  quickFilter === filter
                    ? 'bg-blue-600 text-white'
                    : 'bg-white/5 text-white/60 hover:bg-white/10'
                }`}
              >
                {filter === 'all' ? p('filter_all_types') : categoryLabel(filter)}
              </button>
            ))}
          </div>
        </div>

        {/* Search and Export */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/40 h-5 w-5" />
            <input
              type="text"
              placeholder={p('search_placeholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex gap-2">
            <button className="bg-white/5 hover:bg-white/10 text-white px-6 py-3 rounded-xl font-medium transition-all flex items-center space-x-2 border border-white/10">
              <FileDown className="h-5 w-5" />
              <span>{p('export_pdf')}</span>
            </button>
            <button className="bg-white/5 hover:bg-white/10 text-white px-6 py-3 rounded-xl font-medium transition-all flex items-center space-x-2 border border-white/10">
              <FileDown className="h-5 w-5" />
              <span>{p('export_excel')}</span>
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="glass-table-scroll overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                {isAffectationsColVisible('operation') ? (
                <th className="text-left py-4 px-4 text-white/60 font-medium">{p('col_operation')}</th>
                ) : null}
                {isAffectationsColVisible('assigned_code') ? (
                <th className="text-left py-4 px-4 text-white/60 font-medium">{p('col_assigned_code')}</th>
                ) : null}
                {isAffectationsColVisible('type') ? (
                <th className="text-left py-4 px-4 text-white/60 font-medium">{p('col_type')}</th>
                ) : null}
                {isAffectationsColVisible('date') ? (
                <th className="text-left py-4 px-4 text-white/60 font-medium">{p('col_date')}</th>
                ) : null}
                {isAffectationsColVisible('responsible') ? (
                <th className="text-left py-4 px-4 text-white/60 font-medium">{p('col_responsible')}</th>
                ) : null}
                {isAffectationsColVisible('status') ? (
                <th className="text-left py-4 px-4 text-white/60 font-medium">{p('col_status')}</th>
                ) : null}
                <QawaemTableHeaderActionsCell
                  label={p('col_actions')}
                  columns={affectationsQawaemColumnDefs}
                  onToggle={handleToggleAffectationsColumn}
                  visibleCount={affectationsQawaemVisibleColumnCount}
                  totalCount={affectationsQawaemColumnDefs.length}
                  thClassName="text-right py-4 px-4 text-white/60 font-medium"
                />
              </tr>
            </thead>
            <tbody>
              {filteredAffectations.map((affectation) => (
                <tr key={affectation.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                  {isAffectationsColVisible('operation') ? (
                  <td className="py-4 px-4 text-white font-medium">{affectation.operation_name}</td>
                  ) : null}
                  {isAffectationsColVisible('assigned_code') ? (
                  <td className="py-4 px-4 text-white/80 font-mono">{affectation.code}</td>
                  ) : null}
                  {isAffectationsColVisible('type') ? (
                  <td className="py-4 px-4 text-white/80">{categoryLabel(affectation.operation_type)}</td>
                  ) : null}
                  {isAffectationsColVisible('date') ? (
                  <td className="py-4 px-4 text-white/80">{new Date(affectation.affectation_date).toLocaleDateString(getBrandLocale())}</td>
                  ) : null}
                  {isAffectationsColVisible('responsible') ? (
                  <td className="py-4 px-4 text-white/80">{affectation.responsible}</td>
                  ) : null}
                  {isAffectationsColVisible('status') ? (
                  <td className="py-4 px-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      affectation.status === 'active' ? 'bg-green-500/20 text-green-300' :
                      affectation.status === 'pending' ? 'bg-amber-500/20 text-amber-300' :
                      'bg-gray-500/20 text-gray-300'
                    }`}>
                      {affectation.status === 'active' ? p('status_active') : affectation.status === 'pending' ? p('status_pending') : p('status_inactive')}
                    </span>
                  </td>
                  ) : null}
                  <td className="py-4 px-4">
                    <div className="flex items-center justify-end space-x-2">
                      <button onClick={() => handleView(affectation)} className="p-2 hover:bg-white/10 rounded-lg transition-colors" title={p('view')}>
                        <Eye className="h-4 w-4 text-blue-400" />
                      </button>
                      <button onClick={() => handleEdit(affectation)} className="p-2 hover:bg-white/10 rounded-lg transition-colors" title={p('edit')}>
                        <Edit className="h-4 w-4 text-amber-400" />
                      </button>
                      <button onClick={() => handleDelete(affectation.id)} className="p-2 hover:bg-white/10 rounded-lg transition-colors" title={p('delete')}>
                        <Trash2 className="h-4 w-4 text-red-400" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={resetForm} role="presentation">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} onClick={(e) => e.stopPropagation()} className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl max-h-[90vh] flex flex-col overflow-hidden" style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }}>
            <div className="flex items-center justify-between p-6 border-b border-white/10 shrink-0">
              <h2 className="text-2xl font-bold text-white">{selectedAffectation ? p('modal_edit') : p('modal_new')}</h2>
              <div className="flex items-center gap-3">
                <button type="submit" form="affectation-form" className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"><CheckCircle size={18} />{selectedAffectation ? p('btn_update') : p('btn_create')}</button>
                <button type="button" onClick={resetForm} className="text-white/70 hover:text-white transition-colors"><X size={24} /></button>
              </div>
            </div>
            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="affectation-form" onSubmit={handleSubmit}>
                <div className="space-y-4 pb-6 border-b border-white/10">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_operation_name')} <span className="qawaem-required-mark">*</span></label>
                      <input type="text" required value={formData.operation_name} onChange={(e) => setFormData({ ...formData, operation_name: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                      <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_code')} <span className="qawaem-required-mark">*</span></label>
                      <input type="text" required value={formData.code} onChange={(e) => setFormData({ ...formData, code: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                      <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_operation_type')}</label>
                      <select value={formData.operation_type} onChange={(e) => setFormData({ ...formData, operation_type: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                        <option value="Transport">{p('category_transport')}</option>
                        <option value="Entreposage">{p('category_storage')}</option>
                        <option value="Emballage">{p('category_packaging')}</option>
                        <option value="Manutention">{p('category_handling')}</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="space-y-4 pb-6 border-b border-white/10">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_affectation_date')} <span className="qawaem-required-mark">*</span></label>
                      <input type="date" required value={formData.affectation_date} onChange={(e) => setFormData({ ...formData, affectation_date: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                      <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_responsible')} <span className="qawaem-required-mark">*</span></label>
                      <input type="text" required value={formData.responsible} onChange={(e) => setFormData({ ...formData, responsible: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                      <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_status')}</label>
                      <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                        <option value="active">{p('status_active')}</option>
                        <option value="pending">{p('status_pending')}</option>
                        <option value="inactive">{p('status_inactive')}</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-3">
                      <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_notes')}</label>
                      <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={3} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      {/* View Modal */}
      {showViewModal && selectedAffectation && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={() => setShowViewModal(false)} role="presentation">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white/5 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl p-8 max-h-[90vh] overflow-y-auto shadow-[0_0_40px_rgba(59,130,246,0.4)]"
              style={{ width: '90%', maxWidth: '700px' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">{p('view_title')}</h2>
                <button onClick={() => setShowViewModal(false)} className="text-white/60 hover:text-white">
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <p className="text-white/60 text-sm mb-1">{p('col_operation')}</p>
                  <p className="text-white font-medium">{selectedAffectation.operationName}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-white/60 text-sm mb-1">{p('col_assigned_code')}</p>
                    <p className="text-white font-medium font-mono">{selectedAffectation.code}</p>
                  </div>
                  <div>
                    <p className="text-white/60 text-sm mb-1">{p('col_type')}</p>
                    <p className="text-white font-medium">{categoryLabel(selectedAffectation.operationType)}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-white/60 text-sm mb-1">{p('col_date')}</p>
                    <p className="text-white font-medium">{new Date(selectedAffectation.affectationDate).toLocaleDateString(getBrandLocale())}</p>
                  </div>
                  <div>
                    <p className="text-white/60 text-sm mb-1">{p('col_responsible')}</p>
                    <p className="text-white font-medium">{selectedAffectation.responsible}</p>
                  </div>
                </div>

                <div>
                  <p className="text-white/60 text-sm mb-1">{p('col_status')}</p>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    selectedAffectation.status === 'active' ? 'bg-green-500/20 text-green-300' :
                    selectedAffectation.status === 'pending' ? 'bg-amber-500/20 text-amber-300' :
                    'bg-gray-500/20 text-gray-300'
                  }`}>
                    {selectedAffectation.status === 'active' ? p('status_active') : selectedAffectation.status === 'pending' ? p('status_pending') : p('status_inactive')}
                  </span>
                </div>

                {selectedAffectation.notes && (
                  <div>
                    <p className="text-white/60 text-sm mb-1">{p('label_notes')}</p>
                    <p className="text-white font-medium">{selectedAffectation.notes}</p>
                  </div>
                )}
              </div>

              <button
                onClick={() => setShowViewModal(false)}
                className="w-full mt-6 bg-white/5 hover:bg-white/10 text-white py-3 rounded-xl font-semibold transition-all border border-white/10"
              >
                {p('close')}
              </button>
            </motion.div>
        </div>,
        document.body
      )}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </div>
  );
};


